/* eslint-disable */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import './Map.css';
import * as maptilersdk from '@maptiler/sdk';
import maplibregl, { setWorkerUrl as setMaplibreWorkerUrl } from 'maplibre-gl';
import Supercluster from 'supercluster';
import i18n from '../../i18n';
import { distanceFromYouHtml } from '../../components/DistanceFromYouLabel';
import Spinner from '../../components/loaders/globalSpinner/Spinner';
import { api } from '../../redux/api';
import { useGetClientId } from '../../hookServices/clientId';
import { getCookie, setCookie } from '../../hookServices/cookiesManager';
import { enqueueTrackPoint } from '../../hookServices/evDriverTracker';
import { isDcPort, plugTypeMaping } from '../../utils/utils';
import { stationNameHasEnergyStorage } from '../../utils/openEmsEvPortBattery';
import { getEmbedPortStatusClass } from './embedPortStatus';
import { buildEmbedPortClusterRowsHtml, wrapEmbedClusterWithFeatureBadges } from './embedPortClusterMarkup';
import {
  START_MANY_SHEET_COLLAPSED_TOP_RATIO,
  START_MANY_SHEET_EXPANDED_PEEK_PX,
} from '../startManyPage/startManySheetLayout';
import { useSearchParams } from 'react-router-dom';
import OpenMapsConfirmModal from '../../components/OpenMapsConfirmModal';
import { buildEvuaStationUrl, evuaStationKey } from './mapPortSearchUtils';
import { withSelectedStationSearchParams } from './mapFilterUrl';
import { buildKm220LocationIndex, findKm220MatchForEvuaPort, findNearbySimilarKm220Match } from './evuaKm220Match';
import { resolveEvuaPowerKwDetailsBatch, EVUA_DEFAULT_AC_POWER_KW, EVUA_DEFAULT_POWER_KW } from './evuaKwCache';
import { hasEvuaAcConnector, hasEvuaDcConnector, powerMeetsMinKw, shouldOmitMatchedEvuaPin } from './evuaMapFilters';
import { isGpsFarFromUkraine } from '../../utils/ukraineRegions';
import { openStartManyForStation } from '../startPage/openStartManyForStation';
import { useTranslation } from 'react-i18next';

const API_KEY = '1Lk2s9HJjoiXBR1oqw5a';
maptilersdk.config.apiKey = API_KEY;

// Avoid MapLibre worker code being broken by the bundler (minified "a is not defined" in worker).
const maplibreWorkerUrl = `${process.env.PUBLIC_URL || ''}/maplibre-gl-csp-worker.js`;
if (typeof maptilersdk.setWorkerUrl === 'function') {
  maptilersdk.setWorkerUrl(maplibreWorkerUrl);
}
if (typeof setMaplibreWorkerUrl === 'function') {
  setMaplibreWorkerUrl(maplibreWorkerUrl);
}

const MAP_STYLE_KEY = 'mapStyle';
const MAP_STYLE_ORDER = ['hybrid', 'white', 'night'];

/** ID of the single raster source allowed on the basemap (only present in HYBRID/satellite). */
const SATELLITE_RASTER_SOURCE_ID = 'satellite';

function mapStyleToVector(style) {
  switch (style) {
    case 'hybrid':
      return maptilersdk.MapStyle.HYBRID;
    case 'night':
      // Dataviz dark keeps borders, cities, and roads without the blue Streets palette.
      return maptilersdk.MapStyle.DATAVIZ.DARK;
    case 'white':
    default:
      return maptilersdk.MapStyle.STREETS_V4;
  }
}

/** MapTiler label language follows the UI locale (ua/uk, en, ru). */
function resolveMapLanguage(locale) {
  const lang = String(locale || 'ua').toLowerCase();
  if (lang.startsWith('en')) return maptilersdk.Language.ENGLISH;
  if (lang.startsWith('ru')) return maptilersdk.Language.RUSSIAN;
  return maptilersdk.Language.UKRAINIAN;
}

function applyMapLanguage(mapInstance) {
  if (!mapInstance) return;
  try {
    mapInstance.setLanguage(resolveMapLanguage(i18n.language));
  } catch (_e) {
    /* style may still be loading */
  }
}

function assertVectorBasemap(mapInstance, { satelliteExpected }) {
  try {
    const styleSpec = mapInstance?.getStyle?.();
    const sources = styleSpec?.sources || {};
    const rasterSourceIds = Object.entries(sources)
      .filter(([, src]) => src?.type === 'raster' || src?.type === 'raster-dem')
      .map(([id]) => id);

    if (satelliteExpected) {
      const unexpected = rasterSourceIds.filter(id => id !== SATELLITE_RASTER_SOURCE_ID);
      if (unexpected.length > 0) {
        console.warn('[basemap] unexpected raster sources alongside satellite:', unexpected);
      }
      return;
    }

    if (rasterSourceIds.length > 0) {
      console.warn('[basemap] non-satellite view loaded a style with raster sources:', rasterSourceIds);
    }
  } catch (_e) {
    // Style not ready yet — caller invokes us inside style.load, so this is best-effort.
  }
}

const ZOOM_BUTTON_STEP = 3;
const ZOOM_ANIMATION_MS = 300;

/** Day or night from the device color scheme. Hybrid is opt-in via the style button. */
function getDefaultMapStyle() {
  const prefersDark =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'night' : 'white';
}

function getNextMapStyle(currentStyle) {
  const index = MAP_STYLE_ORDER.indexOf(currentStyle);
  const safeIndex = index >= 0 ? index : 0;
  return MAP_STYLE_ORDER[(safeIndex + 1) % MAP_STYLE_ORDER.length];
}

function saveMapStyle(style) {
  sessionStorage.setItem(MAP_STYLE_KEY, `${getDateKeyFromTimestamp(Date.now())}:${style}`);
}

function getSavedMapStyle() {
  const stored = sessionStorage.getItem(MAP_STYLE_KEY);
  const todayDateKey = getDateKeyFromTimestamp(Date.now());
  if (stored) {
    const separatorIndex = stored.indexOf(':');
    if (separatorIndex > 0) {
      const dateKey = stored.slice(0, separatorIndex);
      const style = stored.slice(separatorIndex + 1);
      if (dateKey === todayDateKey && MAP_STYLE_ORDER.includes(style)) {
        return style;
      }
    }
    sessionStorage.removeItem(MAP_STYLE_KEY);
  }

  // Legacy session keys from the old satellite / force-dark toggle.
  if (sessionStorage.getItem('mapSatelliteView') === 'true') {
    sessionStorage.removeItem('mapSatelliteView');
    saveMapStyle('hybrid');
    return 'hybrid';
  }
  const legacyForceDark = sessionStorage.getItem('mapForceDark');
  if (legacyForceDark === todayDateKey || legacyForceDark === 'true') {
    sessionStorage.removeItem('mapForceDark');
    saveMapStyle('night');
    return 'night';
  }
  sessionStorage.removeItem('mapForceDark');
  return null;
}

function clearSavedMapStyle() {
  sessionStorage.removeItem(MAP_STYLE_KEY);
}

const msUntilLocalMidnight = () => {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime() - now.getTime();
};

function initMapStyleFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('mapDark') === '1' || params.get('mapTheme') === 'dark') {
    saveMapStyle('night');
    return 'night';
  }
  return null;
}

function resolveInitialMapStyle(lat, lng) {
  return initMapStyleFromUrl() || getSavedMapStyle() || getDefaultMapStyle(lat, lng);
}

function isMapStyleDayLight(style) {
  return style === 'white';
}

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/** Rated power in kW for map cluster aggregation. */
function getPortPowerKw(port) {
  const watts = Number(port?.maxPowerWt ?? port?.max_power_wt);
  if (Number.isFinite(watts) && watts > 0) {
    return watts / 1000;
  }
  const apiKw = Number(port?.maxpow ?? port?.minpow);
  if (Number.isFinite(apiKw) && apiKw > 0) {
    return apiKw;
  }
  // EV UA roaming items without rated power: AC default 22 kW, DC default 80 kW.
  if (port?.key != null && port?.lon != null && port?.lat != null) {
    if (hasEvuaAcConnector(port) && !hasEvuaDcConnector(port)) {
      return EVUA_DEFAULT_AC_POWER_KW;
    }
    return EVUA_DEFAULT_POWER_KW;
  }
  return 0;
}

function portHasWebcam(port) {
  const url = port?.webcamUrl ?? port?.webcam_url;
  return typeof url === 'string' && url.trim().length > 0;
}

function portHasEnergyStorage(port) {
  return stationNameHasEnergyStorage(port?.name);
}

/** True when a 220-km (or similar) port is actively charging. */
function portIsCharging(port) {
  return (
    port?.job?.state === 'IN_PROGRESS' ||
    (typeof port?.name === 'string' && (port.name.includes('used') || port.name.includes('IN_PROGRESS')))
  );
}

/** Explicit offline only — unknown/missing `online` stays treated as available (pink cluster). */
function portIsOffline(port) {
  return port?.online === false;
}

function areAllPortsOffline(ports) {
  if (!Array.isArray(ports) || ports.length === 0) {
    return false;
  }
  return ports.every(portIsOffline);
}

const WEBCAM_MARKER_BADGE_HTML =
  '<span class="cluster-marker__camera-label" title="Webcam" aria-hidden="true">' +
  '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M17 10.5V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3.5l5 3.5V7l-5 3.5z"/>' +
  '</svg></span>';

const SOLAR_MARKER_BADGE_HTML =
  '<span class="cluster-marker__solar-label" title="Solar & storage" aria-hidden="true">' +
  '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
  '<circle cx="12" cy="12" r="4"/>' +
  '<path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>' +
  '</svg></span>';

function buildPowerKwDiskInnerHtml(value, unit) {
  const unitHtml = unit ? `<span class="cluster-marker__unit">${unit}</span>` : '';
  return `<span class="cluster-marker__kw"><span class="cluster-marker__value">${value}</span>${unitHtml}</span>`;
}

function applyPowerKwDiskStyles(el, { size, fontSize, title }) {
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.minWidth = `${size}px`;
  el.style.minHeight = `${size}px`;
  el.style.fontSize = `${fontSize}px`;
  el.style.zIndex = '1';
  if (title) {
    el.title = title;
  }
}

/** Root element for MapLibre marker — shell keeps badges glued to the circle during pan/zoom. */
function createPowerKwMarkerElement({
  value,
  unit,
  size,
  fontSize,
  title = '',
  hasWebcam = false,
  hasEnergyStorage = false,
  extraClasses = '',
  blink = false,
  offline = false,
}) {
  const hasBadges = hasWebcam || hasEnergyStorage;
  const extraClassList = extraClasses.split(/\s+/).filter(Boolean);
  const showBlink = blink && !offline;

  if (!hasBadges) {
    const el = document.createElement('div');
    el.className = ['cluster-marker', 'cluster-marker--power-kw', ...extraClassList].join(' ');
    if (offline) {
      el.classList.add('cluster-marker--offline');
    }
    if (showBlink) {
      el.classList.add('blink_me');
    }
    applyPowerKwDiskStyles(el, { size, fontSize, title });
    el.innerHTML = buildPowerKwDiskInnerHtml(value, unit);
    return el;
  }

  const shell = document.createElement('div');
  shell.className = ['cluster-marker-shell', ...extraClassList].join(' ');
  if (offline) {
    shell.classList.add('cluster-marker-shell--offline');
  }
  if (showBlink) {
    shell.classList.add('blink_me');
  }
  if (hasWebcam) {
    shell.classList.add('cluster-marker-shell--has-webcam');
  }
  if (hasEnergyStorage) {
    shell.classList.add('cluster-marker-shell--has-solar');
  }
  if (title) {
    shell.title = title;
  }

  const frame = document.createElement('div');
  frame.className = 'cluster-marker-shell__frame';
  frame.style.width = `${size}px`;
  frame.style.height = `${size}px`;

  const disk = document.createElement('div');
  disk.className = [
    'cluster-marker',
    'cluster-marker--power-kw',
    'cluster-marker-shell__disk',
    offline ? 'cluster-marker--offline' : '',
    ...extraClassList,
  ]
    .filter(Boolean)
    .join(' ');
  applyPowerKwDiskStyles(disk, { size, fontSize });
  disk.innerHTML = buildPowerKwDiskInnerHtml(value, unit);
  frame.appendChild(disk);

  if (hasEnergyStorage) {
    frame.insertAdjacentHTML('beforeend', SOLAR_MARKER_BADGE_HTML);
  }
  if (hasWebcam) {
    frame.insertAdjacentHTML('beforeend', WEBCAM_MARKER_BADGE_HTML);
  }

  shell.appendChild(frame);

  return shell;
}

function isEvuaMapPort(port) {
  return port?.key != null && port?.lon != null && port?.lat != null;
}

/** One map point per deviceId (max kW for DC km220, sum for AC); ports without deviceId stay separate. */
function aggregatePortsByDeviceId(ports) {
  if (!Array.isArray(ports) || ports.length === 0) {
    return [];
  }

  const byDeviceId = new globalThis.Map();
  const standalone = [];

  for (const port of ports) {
    const powerKw = getPortPowerKw(port);
    const deviceId = port?.deviceId != null ? String(port.deviceId).trim() : '';
    const matchedPortNumbers = Array.isArray(port?._matchedPortNumbers)
      ? port._matchedPortNumbers.map(n => String(n).trim()).filter(Boolean)
      : [];
    const portNumber = String(port?.number ?? '').trim();
    const initialPortNumbers = [...new Set([...(portNumber ? [portNumber] : []), ...matchedPortNumbers])];

    if (!deviceId) {
      standalone.push({
        port,
        powerKw,
        portNumbers: initialPortNumbers,
        hasWebcam: portHasWebcam(port),
        hasEnergyStorage: portHasEnergyStorage(port),
        isCharging: portIsCharging(port) || Boolean(port?._matchedIsCharging),
        isOffline: portIsOffline(port),
        isApproximate: Boolean(port?._powerApproximate),
        hasKm220Match: Boolean(port?._km220Match),
      });
      continue;
    }

    const existing = byDeviceId.get(deviceId);
    const isCharging = portIsCharging(port) || Boolean(port?._matchedIsCharging);
    const isOffline = portIsOffline(port);
    const isApproximate = Boolean(port?._powerApproximate);
    if (!existing) {
      byDeviceId.set(deviceId, {
        port,
        powerKw,
        portNumbers: [...initialPortNumbers],
        // Set mirror of portNumbers so merging many ports into one device stays linear.
        portNumbersSeen: new Set(initialPortNumbers),
        hasWebcam: portHasWebcam(port),
        hasEnergyStorage: portHasEnergyStorage(port),
        isCharging,
        isOffline,
        isApproximate,
        hasKm220Match: Boolean(port?._km220Match),
      });
    } else {
      const useMaxPower =
        !isEvuaMapPort(existing.port) &&
        !isEvuaMapPort(port) &&
        (isDcPort(existing.port?.plugType, existing.port?.maxPowerWt ?? existing.port?.max_power_wt) ||
          isDcPort(port?.plugType, port?.maxPowerWt ?? port?.max_power_wt));
      existing.powerKw = useMaxPower ? Math.max(existing.powerKw, powerKw) : existing.powerKw + powerKw;
      // EV UA stations matched to the same 220-km device must not sum kW twice.
      if (
        isEvuaMapPort(existing.port) &&
        isEvuaMapPort(port) &&
        existing.port?.deviceId &&
        port?.deviceId &&
        String(existing.port.deviceId) === String(port.deviceId)
      ) {
        existing.powerKw = Math.max(
          Number(existing.powerKw) || 0,
          Number(powerKw) || 0,
          Number(existing.port?._matchedPowerKw) || 0,
          Number(port?._matchedPowerKw) || 0
        );
      }
      existing.hasWebcam = existing.hasWebcam || portHasWebcam(port);
      existing.hasEnergyStorage = existing.hasEnergyStorage || portHasEnergyStorage(port);
      existing.isCharging = existing.isCharging || isCharging;
      existing.isOffline = Boolean(existing.isOffline) && isOffline;
      existing.isApproximate = existing.isApproximate || isApproximate;
      existing.hasKm220Match = existing.hasKm220Match || Boolean(port?._km220Match);
      for (const num of initialPortNumbers) {
        if (!existing.portNumbersSeen.has(num)) {
          existing.portNumbersSeen.add(num);
          existing.portNumbers.push(num);
        }
      }
    }
  }

  return [...standalone, ...byDeviceId.values()];
}

function buildClusterFeature(entry, extraProperties, lonKey = 'longitude', latKey = 'latitude') {
  const {
    port,
    powerKw,
    portNumbers = [],
    hasWebcam = false,
    hasEnergyStorage = false,
    isCharging = false,
    isOffline = false,
    isApproximate = false,
    hasKm220Match = false,
  } = entry;
  return {
    type: 'Feature',
    properties: {
      port,
      powerKw,
      portNumbers,
      hasWebcam,
      hasEnergyStorage,
      isCharging,
      isOffline,
      isApproximate,
      hasKm220Match,
      ...extraProperties,
    },
    geometry: {
      type: 'Point',
      coordinates: [parseFloat(port[lonKey]), parseFloat(port[latKey])],
    },
  };
}

/** Supercluster radius (px). Larger → fewer clusters, so city names stay readable at country zoom. */
const DEFAULT_CLUSTER_RADIUS_PX = 96;

function createPowerSumClusterIndex() {
  return new Supercluster({
    radius: DEFAULT_CLUSTER_RADIUS_PX,
    maxZoom: 16,
    minPoints: 2,
    map: props => ({
      powerKwSum: Number(props.powerKw) || 0,
      hasWebcam: props.hasWebcam ? 1 : 0,
      hasEnergyStorage: props.hasEnergyStorage ? 1 : 0,
      isCharging: props.isCharging ? 1 : 0,
      // 1 only when every leaf in the cluster is offline (AND via Math.min in reduce).
      isOffline: props.isOffline ? 1 : 0,
      isApproximate: props.isApproximate ? 1 : 0,
      hasKm220Match: props.hasKm220Match ? 1 : 0,
    }),
    reduce: (accumulated, props) => {
      accumulated.powerKwSum += props.powerKwSum;
      accumulated.hasWebcam = Math.max(accumulated.hasWebcam || 0, props.hasWebcam || 0);
      accumulated.hasEnergyStorage = Math.max(accumulated.hasEnergyStorage || 0, props.hasEnergyStorage || 0);
      accumulated.isCharging = Math.max(accumulated.isCharging || 0, props.isCharging || 0);
      accumulated.isOffline = Math.min(accumulated.isOffline || 0, props.isOffline || 0);
      accumulated.isApproximate = Math.max(accumulated.isApproximate || 0, props.isApproximate || 0);
      accumulated.hasKm220Match = Math.max(accumulated.hasKm220Match || 0, props.hasKm220Match || 0);
    },
  });
}

function isEvuaOnlyPowerMarker(type, properties) {
  return type === 'evua' && !properties?.hasKm220Match;
}

function formatClusterPowerLabel(kwSum) {
  const kw = Number(kwSum) || 0;
  if (kw >= 1000) {
    const mw = kw / 1000;
    const raw = mw >= 10 ? String(Math.round(mw)) : mw.toFixed(1).replace(/\.0$/, '');
    return { value: raw, unit: 'MW' };
  }
  return { value: String(Math.round(kw)), unit: 'KW' };
}

function formatClusterPowerTitle(kwSum) {
  const kw = Number(kwSum) || 0;
  if (kw >= 1000) {
    const mw = kw / 1000;
    return `${mw >= 10 ? Math.round(mw) : mw.toFixed(1)} MW`;
  }
  return `${Math.round(kw)} KW`;
}

function getGroupPowerKw(group) {
  if (!Array.isArray(group) || group.length === 0) {
    return 0;
  }
  const isDcGroup = group.some(port => isDcPort(port?.plugType, port?.maxPowerWt ?? port?.max_power_wt));
  if (isDcGroup) {
    return Math.max(...group.map(port => getPortPowerKw(port)));
  }
  return group.reduce((sum, port) => sum + getPortPowerKw(port), 0);
}

function buildStartManyHref(portNumbers) {
  const nums = (portNumbers || []).map(number => String(number).trim()).filter(Boolean);
  if (nums.length === 0) {
    return null;
  }
  return `/startMany?ports=${nums.join(',')}`;
}

function getClusterMarkerDimensions(kwSum) {
  const kw = Math.max(0, Number(kwSum) || 0);
  const MIN_KW = 15;
  const MAX_KW = 6000;
  const MIN_PX = 28;
  // Keep country-level disks small enough that city labels stay visible beside them.
  const MAX_PX = 52;

  if (kw <= MIN_KW) {
    const fontSize = Math.max(11, Math.round(MIN_PX * 0.28));
    return { size: MIN_PX, fontSize };
  }

  const clampedKw = Math.min(kw, MAX_KW);
  // Diameter ~ sqrt(kW) so circle area grows with power (more visible spread than log scale).
  const sqrtMin = Math.sqrt(MIN_KW);
  const sqrtMax = Math.sqrt(MAX_KW);
  const t = (Math.sqrt(clampedKw) - sqrtMin) / (sqrtMax - sqrtMin);
  const size = Math.round(MIN_PX + (MAX_PX - MIN_PX) * Math.min(1, Math.max(0, t)));
  const fontSize = Math.max(11, Math.round(size * 0.28));
  return { size, fontSize };
}

function resolveKm220PinStyle(port) {
  if (port?.name?.includes('promoLink:')) {
    const match = port.name.match(/promoLink:([^\s]+)/);
    return {
      url: '/portRoaming.svg',
      width: '30px',
      height: '30px',
      promoLink: match && match[1] ? stripOcppPortNumberFromPromoLink(match[1]) : null,
    };
  }
  if (port?.name?.includes('inService')) {
    return { url: '/port/inService/1.svg', width: '40px', height: '40px' };
  }
  if (port?.name?.includes('dick')) {
    return { url: '/moscow.svg', width: '40px', height: '40px' };
  }
  if (port?.online === false) {
    return { url: '/port/offline/1.svg', width: '40px', height: '40px' };
  }
  return { url: '/port/online/1.svg', width: '40px', height: '40px' };
}

function stripOcppPortNumberFromPromoLink(url) {
  return url ? url.replace(/ocppPortNumber:(?:\d+|unknown)$/, '') : url;
}

function buildTokaClusterPoints(ports) {
  return aggregatePortsByDeviceId(ports).map(entry => buildClusterFeature(entry, { type: 'toka' }));
}

function buildUgvClusterPoints(ports) {
  return aggregatePortsByDeviceId(ports).map(entry => buildClusterFeature(entry, { type: 'ugv' }));
}

function buildKm220ClusterPoints(ports, { minPowerKw = 0 } = {}) {
  return aggregatePortsByDeviceId(ports.filter(port => !port?.name?.includes('doNotShowOnMap')))
    .filter(entry => powerMeetsMinKw(entry.powerKw, minPowerKw))
    .map(entry => buildClusterFeature(entry, { type: 'km220' }));
}

function buildEvuaClusterPoints(
  ports,
  { matchIndex = null, omitMatched = false, minPowerKw = 0, portFilter = 'all' } = {}
) {
  const matches = matchIndex ? ports.map(port => findKm220MatchForEvuaPort(port, matchIndex)) : null;
  const preferMinPower = portFilter === 'slow';
  // One localStorage read/write for the whole list instead of one per station.
  // `ignoreExplicitWatts` keeps the precedence at EV UA maxpow/minpow → match → cache → default,
  // even when the caller replays ports that already carry an injected maxPowerWt.
  const powers = resolveEvuaPowerKwDetailsBatch(ports, {
    ignoreExplicitWatts: true,
    preferMinPower,
    fallbackKw: preferMinPower ? EVUA_DEFAULT_AC_POWER_KW : EVUA_DEFAULT_POWER_KW,
    getMatchKw: (_port, index) => (preferMinPower ? null : matches?.[index]?.powerKw),
  });

  const normalized = [];
  for (let index = 0; index < ports.length; index++) {
    const port = ports[index];
    const match = matches ? matches[index] : null;
    const { powerKw, approximate } = powers[index];
    // Same GPS cell, or within 50 m when rated power is similar (prod port 4071 vs EV UA «Аврора»).
    const nearbySimilar = !match && omitMatched ? findNearbySimilarKm220Match(port, powerKw, matchIndex) : null;
    if (shouldOmitMatchedEvuaPin(match || nearbySimilar, { omitMatched, minPowerKw })) {
      continue;
    }
    const matchedDeviceId =
      match?.deviceId || (match?.port?.deviceId != null ? String(match.port.deviceId).trim() : '');
    normalized.push({
      ...port,
      longitude: port.lon,
      latitude: port.lat,
      // Prefer 220-km deviceId so multi-port chargers (629+636) share one map point.
      deviceId: matchedDeviceId || port.key,
      // So aggregatePortsByDeviceId / getPortPowerKw use resolved kW (incl. 1-day cache).
      maxPowerWt: powerKw * 1000,
      _powerApproximate: approximate,
      // Marker rendering reuses this instead of re-running the GPS lookup per visible pin.
      _km220Match: match,
      _matchedPowerKw: match?.powerKw,
      _matchedPortNumbers: Array.isArray(match?.portNumbers) ? match.portNumbers : [],
      _matchedHasWebcam: Boolean(match?.hasWebcam),
      _matchedHasEnergyStorage: Boolean(match?.hasEnergyStorage),
      _matchedIsCharging: Boolean(match?.isCharging) || portIsCharging(match?.port),
      _matchedIsOffline: Boolean(match?.isOffline) || portIsOffline(match?.port),
    });
  }
  return aggregatePortsByDeviceId(normalized)
    .filter(entry => powerMeetsMinKw(entry.powerKw, minPowerKw))
    .map(entry => {
      const port = entry.port;
      return buildClusterFeature(
        {
          ...entry,
          hasWebcam: entry.hasWebcam || Boolean(port?._matchedHasWebcam),
          hasEnergyStorage: entry.hasEnergyStorage || Boolean(port?._matchedHasEnergyStorage),
          isCharging: entry.isCharging || Boolean(port?._matchedIsCharging),
          isOffline: Boolean(entry.isOffline) || Boolean(port?._matchedIsOffline),
          isApproximate: entry.isApproximate || Boolean(port?._powerApproximate),
        },
        { type: 'evua' }
      );
    });
}

/** Keyed marker store so pan/zoom can reuse marker DOM instead of recreating every pin. */
function createMarkerStore() {
  return new globalThis.Map();
}

function clearMarkerStore(store) {
  store.forEach(marker => marker.remove());
  store.clear();
}

/**
 * Stable identity for one rendered feature. `generation` changes whenever the Supercluster index is
 * reloaded, so stale content can never be reused; within one generation a marker is reused as long
 * as the same cluster/point stays in the viewport.
 */
function clusterMarkerKey(cluster, type, generation) {
  if (cluster.properties.cluster) {
    return `${generation}|c|${cluster.id}`;
  }
  const port = cluster.properties.port;
  const id = port?.key ?? port?.number ?? port?.deviceId ?? '';
  const [lng, lat] = cluster.geometry.coordinates;
  return `${generation}|p|${type}|${id}|${lng},${lat}`;
}

// Cluster controls component
const ClusterControls = ({ onToggleClustering, clusteringEnabled, onRadiusChange, clusterRadius }) => {
  return (
    <div className="cluster-controls">
      <div className="control-group">
        <label className="control-label">
          <input
            type="checkbox"
            checked={clusteringEnabled}
            onChange={onToggleClustering}
            className="control-checkbox"
          />
          Enable Clustering
        </label>
      </div>
      {clusteringEnabled && (
        <div className="control-group">
          <label className="control-label">
            Cluster Radius: {clusterRadius}px
            <input
              type="range"
              min="40"
              max="160"
              value={clusterRadius}
              onChange={e => onRadiusChange(parseInt(e.target.value))}
              className="control-slider"
            />
          </label>
        </div>
      )}
    </div>
  );
};

/** Earth radius in km */
const EARTH_RADIUS_KM = 6371;
/** GPS farther than this from Ukraine → air-alert warn, keep map on UA at region zoom. */
const FAR_FROM_UKRAINE_KM = 3000;
/** Oblast / region overview zoom (do not fly to foreign GPS). */
const MAP_REGION_LEVEL_ZOOM = 7;
/** Show distance hint at any map zoom (was 12 — too restrictive for wide views). */
const DISTANCE_POPUP_MIN_ZOOM = 0;
/** StartMany embed map: default zoom and fitBounds cap (was 9; −2 levels for wider context). */
const EMBED_MAP_INITIAL_ZOOM = 6;
const EMBED_MAP_FIT_MAX_ZOOM = 11;
/** One camera move into the station + driver frame. No follow-up pans. */
const EMBED_MAP_MOTION_MS = 900;

function embedMapMotionEasing(t) {
  return 1 - (1 - t) ** 3;
}
/** Shift embed map view down so port marker clears the overlapping navbar. */
const EMBED_VIEW_SHIFT_RATIO = 0.2;
const EMBED_FIT_PADDING_BASE = { top: 118, bottom: 76, left: 52, right: 52 };
/** StartMany embed map: station frame until the driver's GPS can share it. */
const EMBED_PORTS_ONLY_TOP_PAD = 96;
const EMBED_PORTS_ONLY_BOTTOM_PAD = 76;
const EMBED_PORTS_ONLY_SIDE_PAD = 48;
/** ~11 m grid — co-located device ports (e.g. startMany 505+506) share one cluster. */
const EMBED_LOCATION_GROUP_DECIMALS = 4;

function getPortLocationGroupKey(port, decimals = 5) {
  const lat = Number(port?.latitude);
  const lng = Number(port?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return `${lat.toFixed(decimals)},${lng.toFixed(decimals)}`;
}

function getEmbedPortGroupKey(port) {
  if (port?.deviceId) {
    return `device:${port.deviceId}`;
  }
  return getPortLocationGroupKey(port, EMBED_LOCATION_GROUP_DECIMALS);
}
const DISTANCE_POPUP_THRESHOLD_KM = 20;
const DISTANCE_POPUP_MAX_PX_FROM_CENTER = 130;
const CAMERA_DATA_URL = '/speed-cameras-scdb.ndjson';
const CAMERA_SOURCE_ID = 'speed-cameras-source';
const CAMERA_LAYER_ID = 'speed-cameras-layer';
const CAMERA_ICON_ID = 'speed-camera-icon';

function haversineDistanceKm(from, to) {
  const [lng1, lat1] = from;
  const [lng2, lat2] = to;
  const r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r;
  const dLng = (lng2 - lng1) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

function buildTrackGeoJson(coords) {
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: coords,
    },
    properties: {},
  };
}

function getDateKeyFromTimestamp(timestamp) {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Local calendar day the driver dismissed the far-GPS air-alert popup. */
const FAR_GPS_AIR_ALERT_DISMISS_LS_KEY = 'farGpsAirAlertDismissedOn';

function isFarGpsAirAlertDismissedToday() {
  try {
    return localStorage.getItem(FAR_GPS_AIR_ALERT_DISMISS_LS_KEY) === getDateKeyFromTimestamp(Date.now());
  } catch (_e) {
    return false;
  }
}

function dismissFarGpsAirAlertForToday() {
  try {
    localStorage.setItem(FAR_GPS_AIR_ALERT_DISMISS_LS_KEY, getDateKeyFromTimestamp(Date.now()));
  } catch (_e) {
    /* private mode / quota */
  }
}

function calculateDrivenDistanceKm(trackCoords) {
  if (!Array.isArray(trackCoords) || trackCoords.length < 2) return 0;

  let totalKm = 0;
  for (let i = 1; i < trackCoords.length; i++) {
    totalKm += haversineDistanceKm(trackCoords[i - 1], trackCoords[i]);
  }
  return totalKm;
}

function calculateTodaySpentUah(historyData) {
  if (!Array.isArray(historyData) || historyData.length === 0) return 0;

  const todayDateKey = getDateKeyFromTimestamp(Date.now());
  return historyData.reduce((sum, item) => {
    const startedOnTs = item?.startedOn ? new Date(item.startedOn).getTime() : null;
    if (!startedOnTs || Number.isNaN(startedOnTs)) return sum;
    if (getDateKeyFromTimestamp(startedOnTs) !== todayDateKey) return sum;
    if (item?.state === 'TOP_UP_BALANCE') return sum;

    const priceInCents = Number(item?.priceInCents || 0);
    if (!Number.isFinite(priceInCents) || priceInCents <= 0) return sum;
    return sum + priceInCents / 100;
  }, 0);
}

function buildCameraGeoJson(cameras) {
  return {
    type: 'FeatureCollection',
    features: cameras.map(camera => ({
      type: 'Feature',
      properties: {
        id: camera.id,
      },
      geometry: {
        type: 'Point',
        coordinates: [camera.lng, camera.lat],
      },
    })),
  };
}

async function loadSpeedCamerasFromJsonl() {
  const response = await fetch(CAMERA_DATA_URL);
  const text = await response.text();
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  const cameras = [];
  for (let i = 0; i < lines.length; i++) {
    try {
      const parsed = JSON.parse(lines[i]);
      if (parsed?._meta) continue;
      if (parsed?.lat == null || parsed?.lon == null) continue;

      cameras.push({
        id: i,
        lat: Number(parsed.lat),
        lng: Number(parsed.lon),
        speedLimitKmh: Number(parsed?.speed || parsed?.spd || 50),
      });
    } catch (_e) {
      // Ignore malformed line
    }
  }

  return cameras;
}

function createCameraIconImage(size = 28) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Outer pin circle
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle = '#ff4d4f';
  ctx.fill();

  // Lens body
  const bodyW = size * 0.56;
  const bodyH = size * 0.3;
  const bodyX = (size - bodyW) / 2;
  const bodyY = size * 0.38;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(bodyX, bodyY, bodyW, bodyH);

  // Lens
  ctx.beginPath();
  ctx.arc(size / 2, bodyY + bodyH / 2, size * 0.09, 0, Math.PI * 2);
  ctx.fillStyle = '#ff4d4f';
  ctx.fill();

  // Top mount
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(size * 0.3, size * 0.26, size * 0.4, size * 0.08);

  return ctx.getImageData(0, 0, size, size);
}

function getMapViewportHeightPx() {
  return window.visualViewport?.height ?? window.innerHeight;
}

function syncMapViewportHeightCss() {
  const heightPx = Math.round(getMapViewportHeightPx());
  document.documentElement.style.setProperty('--map-viewport-height', `${heightPx}px`);
  return heightPx;
}

export default function Map({
  tokaPorts,
  ugvPorts,
  km220Ports,
  km220MatchPorts = [],
  evuaPorts = [],
  evuaStationCatalog = [],
  isLoading = false,
  embedMode = false,
  focusStationNumber = null,
  embedHeight = '20vh',
  onEmbedDistanceKm = null,
  embedPortStatuses = null,
  embedPortsOnly = false,
  embedExtraTopPad = 0,
  embedSheetLayout = false,
  embedSheetExpanded = false,
  onEmbedPortClusterClick = null,
  flyToStation = null,
  minPowerKw = 0,
  portFilter = 'all',
}) {
  const { clientId } = useGetClientId();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: historyData } = api.useGetHistoryDataQuery(clientId, { skip: !clientId });
  const mapContainer = useRef(null);
  const map = useRef(null);
  const lng = 31.223;
  const lat = 49.454;
  const zoom = 4;

  // Cluster state with memoization for better performance
  const [tokaCluster] = useState(createPowerSumClusterIndex);
  const [ugvCluster] = useState(createPowerSumClusterIndex);
  const [km220Cluster] = useState(createPowerSumClusterIndex);
  const [evuaCluster] = useState(createPowerSumClusterIndex);

  const [tokaMarkers] = useState([]);
  const [ugvMarkers] = useState([]);
  const [km220Markers] = useState([]);
  const [evuaClusterMarkers] = useState(createMarkerStore);
  const [evuaMapsTarget, setEvuaMapsTarget] = useState(null);
  const [farGpsAirAlertOpen, setFarGpsAirAlertOpen] = useState(false);
  const onEvuaStationClickRef = useRef(null);
  const km220MatchIndexRef = useRef(new globalThis.Map());
  const preGeolocateViewRef = useRef(null);

  // Memoized so unrelated re-renders (follow-me, basemap toggle, history refetch) do not walk the
  // full 220-km list again; the ref stays assigned during render so effects below see it immediately.
  const km220MatchIndex = useMemo(() => buildKm220LocationIndex(km220MatchPorts, getPortPowerKw), [km220MatchPorts]);
  km220MatchIndexRef.current = km220MatchIndex;

  onEvuaStationClickRef.current = port => {
    if (map.current) {
      const currentZoom = map.current.getZoom();
      const currentCenter = map.current.getCenter();
      sessionStorage.setItem('mapZoom', currentZoom);
      sessionStorage.setItem('mapCenter', JSON.stringify([currentCenter.lng, currentCenter.lat]));
    }
    const key = evuaStationKey(port);
    const latitude = port?.latitude ?? port?.lat;
    const longitude = port?.longitude ?? port?.lon;
    setEvuaMapsTarget({
      latitude,
      longitude,
      evuaUrl: buildEvuaStationUrl(key),
      port,
      portFilter,
      stationKey: key,
    });
    if (!embedMode && key) {
      setSearchParams(withSelectedStationSearchParams(searchParams, key), { replace: true });
    }
  };

  const selectedStationKey = embedMode ? '' : searchParams.get('station') || '';

  useEffect(() => {
    if (embedMode || !selectedStationKey) return undefined;
    const ports = evuaStationCatalog.length ? evuaStationCatalog : evuaPorts;
    const port = ports.find(item => evuaStationKey(item) === selectedStationKey);
    if (!port) return undefined;
    if (evuaStationKey(evuaMapsTarget?.port) === selectedStationKey) return undefined;
    const latitude = port?.latitude ?? port?.lat;
    const longitude = port?.longitude ?? port?.lon;
    setEvuaMapsTarget({
      latitude,
      longitude,
      evuaUrl: buildEvuaStationUrl(evuaStationKey(port)),
      port,
      portFilter,
      stationKey: selectedStationKey,
    });
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (map.current && Number.isFinite(lat) && Number.isFinite(lng)) {
      map.current.flyTo({
        center: [lng, lat],
        zoom: Math.max(map.current.getZoom?.() || 0, 14),
        essential: true,
      });
    }
    return undefined;
  }, [embedMode, selectedStationKey, evuaStationCatalog, evuaPorts, evuaMapsTarget, portFilter]);

  // Cluster markers state
  const [tokaClusterMarkers] = useState(createMarkerStore);
  const [ugvClusterMarkers] = useState(createMarkerStore);
  const [km220ClusterMarkers] = useState(createMarkerStore);
  // Bumped on every Supercluster reload so a data change forces fresh marker DOM.
  const clusterGenerationRef = useRef({ toka: 0, ugv: 0, km220: 0, evua: 0 });

  // Clustering controls state
  const [clusteringEnabled, setClusteringEnabled] = useState(!embedMode);
  const [clusterRadius, setClusterRadius] = useState(DEFAULT_CLUSTER_RADIUS_PX);

  // Follow me state
  const [isFollowing, setIsFollowing] = useState(false);
  const isFollowingRef = useRef(false);
  const geolocateControlRef = useRef(null);
  const embedUserMarkerRef = useRef(null);
  const embedPortsRef = useRef([]);
  const embedPortStatusesRef = useRef({});
  const embedPortsOnlyRef = useRef(embedPortsOnly);
  const embedSheetLayoutRef = useRef(embedSheetLayout);
  const embedSheetExpandedRef = useRef(embedSheetExpanded);
  const embedExtraTopPadRef = useRef(embedExtraTopPad);
  const embedOnPortClusterClickRef = useRef(onEmbedPortClusterClick);
  const focusStationNumberRef = useRef(focusStationNumber);
  focusStationNumberRef.current = focusStationNumber;
  embedPortsOnlyRef.current = embedPortsOnly;
  embedSheetLayoutRef.current = embedSheetLayout;
  embedSheetExpandedRef.current = embedSheetExpanded;
  embedExtraTopPadRef.current = embedExtraTopPad;
  embedOnPortClusterClickRef.current = onEmbedPortClusterClick;
  const embedUserInteractedRef = useRef(false);
  const embedLayoutPortsKeyRef = useRef('');
  const embedGeoWatchRef = useRef(false);
  const embedFittedLngLatRef = useRef(null);
  const isFirstGeolocateClick = useRef(true);
  const userLngLatRef = useRef(null);
  const distancePopupRef = useRef(null);
  const dailyStatsPopupRef = useRef(null);
  const updateDistancePopupIfNeededRef = useRef(() => {});
  const updateDailyStatsPopupIfNeededRef = useRef(() => {});
  const updateCameraPopupIfNeededRef = useRef(() => {});
  const trackPointsRef = useRef([]);
  const lastTrackSaveRef = useRef(null);
  const lastTrackSampleTsRef = useRef(null);
  const isTrackRecordingStoppedRef = useRef(false);
  const speedCamerasRef = useRef([]);
  const lastNotifiedCameraRef = useRef(null);
  const lastCameraSoundTsRef = useRef(0);
  const audioContextRef = useRef(null);
  const updateClusterMarkersRef = useRef(() => {});
  const createClusterElementRef = useRef(() => document.createElement('div'));
  // Assigned during render below; index rebuilds run in effects, so it is always set by then.
  const updateClustersRef = useRef(() => {});

  const TRACK_SAVE_INTERVAL_MS = 30 * 1000;
  const TRACK_LS_KEY = 'userTrackPoints';
  const TRACK_SOURCE_ID = 'user-track-source';
  const TRACK_LAYER_ID = 'user-track-layer';
  const TRACK_MIN_STORE_DISTANCE_KM = 1;
  const TRACK_STOP_DISTANCE_KM = 500;
  const TRACK_MAX_REASONABLE_SPEED_KMH = 350;
  const DAILY_STATS_MIN_DRIVEN_KM = 20;
  const CAMERA_NOTIFY_DISTANCE_KM = 0.5;
  const CAMERA_VISIBLE_DISTANCE_KM = 3;
  const CAMERA_SPEED_BUFFER_KMH = 20;
  const CAMERA_SOUND_COOLDOWN_MS = 30000;

  // Map basemap style: hybrid | white | night (saved in sessionStorage until local midnight)
  const initialMapStyle = resolveInitialMapStyle(lat, lng);
  const [mapStyle, setMapStyle] = useState(initialMapStyle);
  const mapStyleRef = useRef(initialMapStyle);
  const satelliteButtonRef = useRef(null);

  // SVG icons for map style toggle (monochrome violet)
  const earthIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="violet" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`;
  const mapIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="violet" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>`;
  const moonIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="violet" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>`;
  // North triangle is dark; south is light. Same glyph as MapLibre's compass.
  const compassIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 29" aria-hidden="true"><path fill="#666" d="m10.5 14 4-8 4 8z"/><path fill="#ccc" d="m10.5 16 4 8 4-8z"/></svg>`;
  const mapStyleExpiryTimerRef = useRef(null);
  const deviceThemeMediaRef = useRef(null);
  const deviceThemeListenerRef = useRef(null);

  const embedFocusPorts = useMemo(() => {
    if (!embedMode) {
      return km220Ports;
    }
    return (km220Ports || []).filter(p => p?.latitude != null && p?.longitude != null);
  }, [embedMode, km220Ports]);

  // Memoized port data for better performance
  const memoizedTokaPorts = useMemo(() => (embedMode ? [] : tokaPorts), [embedMode, tokaPorts]);
  const memoizedUgvPorts = useMemo(() => (embedMode ? [] : ugvPorts), [embedMode, ugvPorts]);
  const memoizedKm220Ports = useMemo(
    () => (embedMode ? embedFocusPorts : km220Ports),
    [embedMode, embedFocusPorts, km220Ports]
  );

  embedPortsRef.current = memoizedKm220Ports;
  embedPortStatusesRef.current = embedPortStatuses || {};
  const memoizedEvuaPorts = useMemo(() => (embedMode ? [] : evuaPorts), [embedMode, evuaPorts]);

  // Rebuild Supercluster indices when ports or radius change (radius is applied at load time).
  // One effect per source so toggling a single filter does not rebuild all four KD-trees, and each
  // reload bumps the layer generation so markers are rebuilt from the new data.
  useEffect(() => {
    tokaCluster.options.radius = clusterRadius;
    if (memoizedTokaPorts?.length > 0) {
      tokaCluster.load(buildTokaClusterPoints(memoizedTokaPorts));
    }
    clusterGenerationRef.current.toka += 1;
    updateClustersRef.current();
  }, [clusterRadius, tokaCluster, memoizedTokaPorts]);

  useEffect(() => {
    ugvCluster.options.radius = clusterRadius;
    if (memoizedUgvPorts?.length > 0) {
      ugvCluster.load(buildUgvClusterPoints(memoizedUgvPorts));
    }
    clusterGenerationRef.current.ugv += 1;
    updateClustersRef.current();
  }, [clusterRadius, ugvCluster, memoizedUgvPorts]);

  useEffect(() => {
    km220Cluster.options.radius = clusterRadius;
    if (memoizedKm220Ports?.length > 0) {
      km220Cluster.load(buildKm220ClusterPoints(memoizedKm220Ports, { minPowerKw }));
    }
    clusterGenerationRef.current.km220 += 1;
    updateClustersRef.current();
  }, [clusterRadius, km220Cluster, memoizedKm220Ports, minPowerKw]);

  useEffect(() => {
    evuaCluster.options.radius = clusterRadius;
    if (memoizedEvuaPorts?.length > 0) {
      // When 220-km pins are visible, drop GPS-matched EV UA rows so roaming does not double-draw.
      const omitMatched = Boolean(memoizedKm220Ports?.length);
      evuaCluster.load(
        buildEvuaClusterPoints(memoizedEvuaPorts, {
          matchIndex: km220MatchIndex,
          omitMatched,
          minPowerKw,
          portFilter,
        })
      );
    }
    clusterGenerationRef.current.evua += 1;
    updateClustersRef.current();
  }, [clusterRadius, evuaCluster, memoizedEvuaPorts, memoizedKm220Ports, km220MatchIndex, minPowerKw, portFilter]);

  useEffect(() => {
    if (map.current) {
      return;
    } // stops map from intializing more than once

    const focusPort = embedMode ? embedFocusPorts[0] : null;
    const focusLng = focusPort ? parseFloat(focusPort.longitude) : lng;
    const focusLat = focusPort ? parseFloat(focusPort.latitude) : lat;

    // Restore saved map state if it exists (full-page map only)
    const savedZoom = embedMode ? null : sessionStorage.getItem('mapZoom');
    const savedCenter = embedMode ? null : sessionStorage.getItem('mapCenter');

    // Device day/night unless a style was saved for today or forced by URL.
    const activeMapStyle = resolveInitialMapStyle(lat, lng);
    mapStyleRef.current = activeMapStyle;
    setMapStyle(activeMapStyle);
    const initialMapStyleConfig = mapStyleToVector(activeMapStyle);

    map.current = new maptilersdk.Map({
      container: mapContainer.current,
      style: initialMapStyleConfig,
      language: resolveMapLanguage(i18n.language),
      center: savedCenter ? JSON.parse(savedCenter) : [focusLng, focusLat],
      zoom: savedZoom ? parseFloat(savedZoom) : embedMode ? EMBED_MAP_INITIAL_ZOOM : zoom,
      geolocateControl: false,
      navigationControl: false,
      maptilerLogo: false,
      attributionControl: false,
      scaleControl: !embedMode,
      maxZoom: 21,
      ...(embedMode
        ? {
            scrollZoom: true,
            boxZoom: false,
            dragRotate: false,
            dragPan: true,
            keyboard: false,
            doubleClickZoom: true,
            touchZoomRotate: true,
            touchPitch: false,
            cooperativeGestures: false,
          }
        : {}),
    });

    map.current.once('style.load', () => {
      applyMapLanguage(map.current);
      assertVectorBasemap(map.current, { satelliteExpected: activeMapStyle === 'hybrid' });
    });

    const onLanguageChanged = () => {
      applyMapLanguage(map.current);
    };
    i18n.on('languageChanged', onLanguageChanged);

    // Redirect to Google Street View when zoom reaches maximum
    let streetViewPopupShown = false;

    // Disable marker transitions during zoom/drag to prevent animation lag
    const disableMarkerTransitions = () => {
      if (mapContainer.current) mapContainer.current.classList.add('map-interacting');
    };
    const enableMarkerTransitions = () => {
      if (mapContainer.current) mapContainer.current.classList.remove('map-interacting');
    };
    map.current.on('movestart', disableMarkerTransitions);
    map.current.on('zoomstart', disableMarkerTransitions);
    map.current.on('moveend', enableMarkerTransitions);
    map.current.on('zoomend', enableMarkerTransitions);

    map.current.on('zoomend', () => {
      if (embedMode) {
        return;
      }
      const currentZoom = map.current.getZoom();
      if (currentZoom >= 21 && !streetViewPopupShown) {
        streetViewPopupShown = true;
        const center = map.current.getCenter();

        // Create popup overlay
        const overlay = document.createElement('div');
        overlay.className = 'street-view-popup-overlay';
        overlay.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(4px);
        `;

        // Create popup container
        const popup = document.createElement('div');
        popup.className = 'street-view-popup';
        popup.style.cssText = `
          background: white;
          border-radius: 16px;
          padding: 24px 32px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          animation: popupFadeIn 0.2s ease-out;
        `;

        // Create title
        const title = document.createElement('div');
        title.style.cssText = `
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin-bottom: 20px;
        `;
        title.textContent = i18n.t('streetView.title');

        // Create buttons container
        const buttons = document.createElement('div');
        buttons.style.cssText = `
          display: flex;
          gap: 12px;
          justify-content: center;
        `;

        // Create Yes button
        const yesBtn = document.createElement('button');
        yesBtn.textContent = i18n.t('streetView.yes');
        yesBtn.style.cssText = `
          padding: 10px 32px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
          color: white;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        `;
        yesBtn.onmouseenter = () => {
          yesBtn.style.transform = 'scale(1.05)';
          yesBtn.style.boxShadow = '0 4px 12px rgba(155, 89, 182, 0.4)';
        };
        yesBtn.onmouseleave = () => {
          yesBtn.style.transform = 'scale(1)';
          yesBtn.style.boxShadow = 'none';
        };
        yesBtn.onclick = () => {
          const streetViewUrl = `https://www.google.com/maps?layer=c&cbll=${center.lat},${center.lng}`;
          window.open(streetViewUrl, '_blank');
          overlay.remove();
          streetViewPopupShown = false;
          map.current.setZoom(20);
        };

        // Create No button
        const noBtn = document.createElement('button');
        noBtn.textContent = i18n.t('streetView.no');
        noBtn.style.cssText = `
          padding: 10px 32px;
          border: 2px solid #ddd;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          background: white;
          color: #666;
          transition: transform 0.15s ease, border-color 0.15s ease;
        `;
        noBtn.onmouseenter = () => {
          noBtn.style.transform = 'scale(1.05)';
          noBtn.style.borderColor = '#9b59b6';
        };
        noBtn.onmouseleave = () => {
          noBtn.style.transform = 'scale(1)';
          noBtn.style.borderColor = '#ddd';
        };
        noBtn.onclick = () => {
          overlay.remove();
          streetViewPopupShown = false;
          map.current.setZoom(20);
        };

        // Assemble popup
        buttons.appendChild(yesBtn);
        buttons.appendChild(noBtn);
        popup.appendChild(title);
        popup.appendChild(buttons);
        overlay.appendChild(popup);

        // Close on overlay click
        overlay.onclick = e => {
          if (e.target === overlay) {
            overlay.remove();
            streetViewPopupShown = false;
            map.current.setZoom(20);
          }
        };

        document.body.appendChild(overlay);
      }
    });

    drawGeolocateControl();

    return () => {
      i18n.off('languageChanged', onLanguageChanged);
      if (deviceThemeMediaRef.current && deviceThemeListenerRef.current) {
        deviceThemeMediaRef.current.removeEventListener('change', deviceThemeListenerRef.current);
        deviceThemeListenerRef.current = null;
      }
      if (mapStyleExpiryTimerRef.current) {
        window.clearTimeout(mapStyleExpiryTimerRef.current);
        mapStyleExpiryTimerRef.current = null;
      }
      if (map.current) {
        try {
          map.current.remove();
        } catch (_e) {
          /* ignore */
        }
        map.current = null;
      }
      if (embedUserMarkerRef.current) {
        embedUserMarkerRef.current.remove();
        embedUserMarkerRef.current = null;
      }
    };
  }, []);

  // Latest inputs for the single debounced cluster update below.
  const clusterInputsRef = useRef(null);
  clusterInputsRef.current = {
    clusteringEnabled,
    tokaPorts: memoizedTokaPorts,
    ugvPorts: memoizedUgvPorts,
    km220Ports: memoizedKm220Ports,
    evuaPorts: memoizedEvuaPorts,
  };

  // Update clusters when map moves. Created once and fed from refs, so the map listeners are
  // registered a single time and no pending timer from a previous data set can fire late.
  const updateClusters = useMemo(
    () =>
      debounce(() => {
        if (!map.current) return;
        const { clusteringEnabled: enabled, tokaPorts, ugvPorts, km220Ports, evuaPorts } = clusterInputsRef.current;
        if (!enabled) return;

        // Don't update clusters if we're in the middle of a drag animation
        const isDragging = map.current.isMoving() || map.current.isZooming();
        if (isDragging) return;

        const bounds = map.current.getBounds();
        const zoom = Math.floor(map.current.getZoom());
        const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];

        // Update toka clusters
        if (tokaPorts?.length > 0) {
          updateClusterMarkersRef.current(tokaCluster.getClusters(bbox, zoom), tokaClusterMarkers, 'toka');
        } else {
          clearMarkerStore(tokaClusterMarkers);
        }

        // Update ugv clusters
        if (ugvPorts?.length > 0) {
          updateClusterMarkersRef.current(ugvCluster.getClusters(bbox, zoom), ugvClusterMarkers, 'ugv');
        } else {
          clearMarkerStore(ugvClusterMarkers);
        }

        // Update km220 clusters (ports from station table; 160+ kW chip filters at index load)
        if (km220Ports?.length > 0) {
          updateClusterMarkersRef.current(km220Cluster.getClusters(bbox, zoom), km220ClusterMarkers, 'km220');
        } else {
          clearMarkerStore(km220ClusterMarkers);
        }

        // EVUA roaming ports — kW sum clusters (same style as 220-km)
        if (evuaPorts?.length > 0) {
          updateClusterMarkersRef.current(evuaCluster.getClusters(bbox, zoom), evuaClusterMarkers, 'evua');
        } else {
          clearMarkerStore(evuaClusterMarkers);
        }

        updateDistancePopupIfNeededRef.current();
      }, 100),
    []
  );

  updateClustersRef.current = updateClusters;

  function createKm220MapPinElement(
    port,
    {
      powerKw,
      portNumbers,
      saveMapState = true,
      hasWebcam: hasWebcamProp,
      hasEnergyStorage: hasEnergyStorageProp,
      blink: blinkProp,
      offline: offlineProp,
    }
  ) {
    const pinStyle = resolveKm220PinStyle(port);
    const kw = Number(powerKw) || getPortPowerKw(port);
    const { value, unit } = formatClusterPowerLabel(kw);
    const { size, fontSize } = getClusterMarkerDimensions(kw);
    const hasWebcam = hasWebcamProp ?? portHasWebcam(port);
    const hasEnergyStorage = hasEnergyStorageProp ?? portHasEnergyStorage(port);
    const blink = blinkProp ?? portIsCharging(port);
    const offline = offlineProp ?? portIsOffline(port);

    const el = createPowerKwMarkerElement({
      value,
      unit,
      size,
      fontSize,
      title: formatClusterPowerTitle(kw),
      hasWebcam,
      hasEnergyStorage,
      extraClasses: 'km220-power-round individual-marker',
      blink,
      offline,
    });

    el.addEventListener('click', () => {
      if (saveMapState && map.current && !embedMode) {
        const currentZoom = map.current.getZoom();
        const currentCenter = map.current.getCenter();
        sessionStorage.setItem('mapZoom', currentZoom);
        sessionStorage.setItem('mapCenter', JSON.stringify([currentCenter.lng, currentCenter.lat]));
      }
      if (pinStyle.promoLink) {
        window.open(pinStyle.promoLink);
        return;
      }
      const fallbackNumber = portNumbers?.length ? portNumbers[0] : port?.number;
      // Expand to all ports on the same deviceId (e.g. 629 → 629,636).
      openStartManyForStation(
        path => {
          window.location.href = path;
        },
        port,
        fallbackNumber
      ).catch(() => {
        const href = buildStartManyHref(portNumbers?.length ? portNumbers : [port?.number]);
        if (href) {
          window.location.href = href;
        }
      });
    });

    return el;
  }

  // Create cluster element (must be defined before updateClusterMarkers)
  createClusterElementRef.current = (cluster, type) => {
    const hasWebcam = Boolean(cluster.properties.hasWebcam);
    const hasEnergyStorage = Boolean(cluster.properties.hasEnergyStorage);
    const blink = Boolean(cluster.properties.isCharging);
    const offline = Boolean(cluster.properties.isOffline);
    const evuaOnly = isEvuaOnlyPowerMarker(type, cluster.properties);

    const kwSum = cluster.properties.powerKwSum ?? 0;
    const { value, unit } = formatClusterPowerLabel(kwSum);
    const { size, fontSize } = getClusterMarkerDimensions(kwSum);

    return createPowerKwMarkerElement({
      value,
      unit,
      size,
      fontSize,
      title: formatClusterPowerTitle(kwSum),
      hasWebcam,
      hasEnergyStorage,
      blink,
      offline,
      extraClasses: evuaOnly ? 'cluster-marker--evua-only' : '',
    });
  };

  // Build the marker for one Supercluster feature (cluster disk or individual pin).
  const createClusterMarker = useCallback((cluster, type) => {
    if (cluster.properties.cluster) {
      // This is a cluster
      const el = createClusterElementRef.current(cluster, type);
      const marker = new maptilersdk.Marker({ element: el, anchor: 'center' })
        .setLngLat(cluster.geometry.coordinates)
        .addTo(map.current);

      el.addEventListener('click', () => {
        // Stop following when user clicks on cluster
        if (isFollowingRef.current) {
          isFollowingRef.current = false;
          setIsFollowing(false);
        }
        const currentZoom = map.current.getZoom();
        const newZoom = Math.min(currentZoom + 4, 20);
        map.current.flyTo({
          center: cluster.geometry.coordinates,
          zoom: newZoom,
          duration: 500,
        });
      });

      return marker;
    }

    // This is an individual point
    const port = cluster.properties.port;

    if (type === 'km220') {
      const powerKw = cluster.properties.powerKw ?? cluster.properties.powerKwSum ?? getPortPowerKw(port);
      const portNumbers = cluster.properties.portNumbers?.length
        ? cluster.properties.portNumbers
        : [port?.number].filter(Boolean);
      const el = createKm220MapPinElement(port, {
        powerKw,
        portNumbers,
        hasWebcam: cluster.properties.hasWebcam || portHasWebcam(port),
        hasEnergyStorage: cluster.properties.hasEnergyStorage || portHasEnergyStorage(port),
        blink: Boolean(cluster.properties.isCharging) || portIsCharging(port),
        offline: Boolean(cluster.properties.isOffline) || portIsOffline(port),
      });
      return new maptilersdk.Marker({ element: el, anchor: 'center' })
        .setLngLat(cluster.geometry.coordinates)
        .addTo(map.current);
    }

    if (type === 'evua') {
      // Match and kW were resolved (and cached) once in buildEvuaClusterPoints.
      const km220Match = port?._km220Match ?? null;
      if (km220Match) {
        const el = createKm220MapPinElement(km220Match.port, {
          powerKw: km220Match.powerKw,
          portNumbers: km220Match.portNumbers,
          hasWebcam: km220Match.hasWebcam,
          hasEnergyStorage: km220Match.hasEnergyStorage,
          blink: Boolean(km220Match.isCharging) || portIsCharging(km220Match.port),
          offline: Boolean(km220Match.isOffline) || portIsOffline(km220Match.port),
        });
        return new maptilersdk.Marker({ element: el, anchor: 'center' })
          .setLngLat(cluster.geometry.coordinates)
          .addTo(map.current);
      }

      const powerKw = Number(port?.maxPowerWt) / 1000;
      const { value, unit } = formatClusterPowerLabel(powerKw);
      const { size, fontSize } = getClusterMarkerDimensions(powerKw);
      const el = createPowerKwMarkerElement({
        value,
        unit,
        size,
        fontSize,
        title: formatClusterPowerTitle(powerKw),
        extraClasses: 'evua-power-round individual-marker cluster-marker--evua-only',
      });
      el.addEventListener('click', () => {
        onEvuaStationClickRef.current?.(port);
      });
      return new maptilersdk.Marker({ element: el, anchor: 'center' })
        .setLngLat(cluster.geometry.coordinates)
        .addTo(map.current);
    }

    const { marker: markerData, el } = drawMarkerWithLocation(port);

    // Apply styling based on port type
    if (type === 'toka') {
      el.style.backgroundImage = port?.name?.includes('OVERPRICED')
        ? 'url(/portRoaming20.svg)'
        : `url(/portRoaming.svg)`;
      el.style.width = port?.name?.includes('OVERPRICED') ? '20px' : '30px';
      el.style.height = port?.name?.includes('OVERPRICED') ? '20px' : '30px';
      el.className = port?.name?.includes('IN_PROGRESS') ? 'blink_me individual-marker' : 'individual-marker';

      el.addEventListener('click', function () {
        window.open(port?.deviceId);
      });
    } else if (type === 'ugv') {
      el.style.backgroundImage = `url(/portRoaming.svg)`;
      el.style.width = '30px';
      el.style.height = '30px';
      el.className = 'individual-marker';

      el.addEventListener('click', function () {
        window.open(port?.deviceId);
      });
    }

    return new maptilersdk.Marker({ element: el }).setLngLat(markerData.geometry.coordinates).addTo(map.current);
  }, []);

  // Sync the keyed marker store with the features currently in view: drop what left, add what is new,
  // and keep everything else (its DOM and click handler are still valid).
  const updateClusterMarkers = useCallback(
    (clusters, markers, type) => {
      // Don't update if map is currently being dragged
      if (map.current && (map.current.isMoving() || map.current.isZooming())) {
        return;
      }

      const generation = clusterGenerationRef.current[type] ?? 0;
      const visibleKeys = new Set();
      const missing = [];

      clusters.forEach(cluster => {
        const key = clusterMarkerKey(cluster, type, generation);
        if (visibleKeys.has(key)) {
          return;
        }
        visibleKeys.add(key);
        if (!markers.has(key)) {
          missing.push([key, cluster]);
        }
      });

      if (missing.length === 0 && visibleKeys.size === markers.size) {
        return;
      }

      markers.forEach((marker, key) => {
        if (!visibleKeys.has(key)) {
          marker.remove();
          markers.delete(key);
        }
      });

      missing.forEach(([key, cluster]) => {
        const marker = createClusterMarker(cluster, type);
        if (marker) {
          markers.set(key, marker);
        }
      });
    },
    [createClusterMarker]
  );

  updateClusterMarkersRef.current = updateClusterMarkers;

  // Toggle clustering
  const handleToggleClustering = useCallback(() => {
    setClusteringEnabled(prev => !prev);
  }, []);

  // Handle radius change
  const handleRadiusChange = useCallback(newRadius => {
    setClusterRadius(newRadius);
  }, []);

  updateDistancePopupIfNeededRef.current = () => {
    if (distancePopupRef.current) {
      distancePopupRef.current.remove();
      distancePopupRef.current = null;
    }
    const user = userLngLatRef.current;
    if (!user || !map.current) return;

    const coords = [];
    const markerGroups = clusteringEnabled
      ? [tokaClusterMarkers, ugvClusterMarkers, km220ClusterMarkers, evuaClusterMarkers]
      : [tokaMarkers, ugvMarkers, km220Markers];
    // Cluster layers keep markers in a keyed store, legacy layers in arrays; both expose forEach.
    markerGroups.forEach(group => {
      group.forEach(m => {
        try {
          const ll = m.getLngLat();
          if (ll != null) coords.push([ll.lng, ll.lat]);
        } catch (_e) {
          /* detached marker */
        }
      });
    });

    const z = map.current.getZoom();
    if (z < DISTANCE_POPUP_MIN_ZOOM || coords.length === 0) return;

    let centerPx;
    try {
      const center = map.current.getCenter();
      centerPx = map.current.project([center.lng, center.lat]);
    } catch (_e) {
      return;
    }

    let best = null;
    let bestD = Infinity;
    for (let i = 0; i < coords.length; i++) {
      const c = coords[i];
      try {
        const p = map.current.project(c);
        const d = Math.hypot(p.x - centerPx.x, p.y - centerPx.y);
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      } catch (_e) {
        /* ignore */
      }
    }
    if (!best || bestD > DISTANCE_POPUP_MAX_PX_FROM_CENTER) return;

    const km = haversineDistanceKm(user, best);
    if (km <= DISTANCE_POPUP_THRESHOLD_KM) return;

    const kmRounded = km >= 100 ? Math.round(km) : Math.round(km * 10) / 10;
    const isDayLightMap = isMapStyleDayLight(mapStyleRef.current);
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: true,
      // Anchor bottom: popup sits above the port/cluster (no tail — see Map.css)
      offset: [0, -40],
      className: `map-distance-glass-popup${isDayLightMap ? ' map-distance-glass-popup-daylight' : ''}`,
      maxWidth: '320px',
      anchor: 'bottom',
    })
      .setLngLat(best)
      .setHTML(
        `<div class="map-distance-glass-inner"><span class="map-distance-glass-label">${distanceFromYouHtml(kmRounded)}</span></div>`
      )
      .addTo(map.current);

    const popupEl = popup.getElement();
    if (popupEl) {
      popupEl.style.zIndex = '40';
    }

    distancePopupRef.current = popup;
  };

  updateDailyStatsPopupIfNeededRef.current = () => {
    if (dailyStatsPopupRef.current) {
      dailyStatsPopupRef.current.remove();
      dailyStatsPopupRef.current = null;
    }

    const user = userLngLatRef.current;
    if (!user || !map.current) return;

    const drivenKm = calculateDrivenDistanceKm(trackPointsRef.current);
    if (drivenKm <= DAILY_STATS_MIN_DRIVEN_KM) return;

    const roundedDrivenKm = drivenKm >= 100 ? Math.round(drivenKm) : Math.round(drivenKm * 10) / 10;
    const spentUah = calculateTodaySpentUah(historyData || []);
    const spentUahText = spentUah.toFixed(2);

    const isDayLightMap = isMapStyleDayLight(mapStyleRef.current);
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: true,
      offset: [0, -28],
      className: `map-distance-glass-popup map-daily-stats-popup ${
        isDayLightMap ? 'map-distance-glass-popup-daylight' : ''
      }`.trim(),
      maxWidth: '320px',
      anchor: 'bottom',
    })
      .setLngLat(user)
      .setHTML(
        `<div class="map-distance-glass-inner"><span class="map-distance-glass-label">${i18n.t(
          'map.todayDrivenCompact',
          {
            km: roundedDrivenKm,
          }
        )}${
          spentUah > 0
            ? `<br/>${i18n.t('map.todaySpentCompact', {
                uah: spentUahText,
              })}`
            : ''
        }</span></div>`
      )
      .addTo(map.current);

    const dailyStatsEl = popup.getElement();
    if (dailyStatsEl) {
      dailyStatsEl.style.zIndex = '40';
    }

    dailyStatsPopupRef.current = popup;
  };

  updateCameraPopupIfNeededRef.current = user => {
    if (!map.current || !user || speedCamerasRef.current.length === 0) {
      lastNotifiedCameraRef.current = null;
      return;
    }

    let nearestCamera = null;
    let nearestDistanceKm = Infinity;
    for (let i = 0; i < speedCamerasRef.current.length; i++) {
      const camera = speedCamerasRef.current[i];
      const d = haversineDistanceKm(user, [camera.lng, camera.lat]);
      if (d < nearestDistanceKm) {
        nearestDistanceKm = d;
        nearestCamera = camera;
      }
    }

    if (!nearestCamera || nearestDistanceKm > CAMERA_NOTIFY_DISTANCE_KM) {
      lastNotifiedCameraRef.current = null;
      return;
    }

    const speedLimitKmh = Number.isFinite(nearestCamera.speedLimitKmh) ? nearestCamera.speedLimitKmh : 50;
    const warningSpeedKmh = speedLimitKmh + CAMERA_SPEED_BUFFER_KMH;
    const distanceMeters = Math.round(nearestDistanceKm * 1000);

    const now = Date.now();
    const shouldPlaySound =
      lastNotifiedCameraRef.current !== nearestCamera.id ||
      now - lastCameraSoundTsRef.current > CAMERA_SOUND_COOLDOWN_MS;

    if (shouldPlaySound) {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          if (!audioContextRef.current) {
            audioContextRef.current = new AudioCtx();
          }
          const ctx = audioContextRef.current;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(950, ctx.currentTime);
          gain.gain.setValueAtTime(0.001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.24);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.25);
        }
      } catch (_e) {
        // Ignore audio failures (e.g. browser policy)
      }
      lastCameraSoundTsRef.current = now;
    }

    lastNotifiedCameraRef.current = nearestCamera.id;
  };

  const updateVisibleCamerasAroundUser = useCallback(user => {
    if (!map.current || !user) return;
    const cameraSource = map.current.getSource(CAMERA_SOURCE_ID);
    if (!cameraSource) return;

    const nearbyCameras = speedCamerasRef.current.filter(camera => {
      const distanceKm = haversineDistanceKm(user, [camera.lng, camera.lat]);
      return distanceKm <= CAMERA_VISIBLE_DISTANCE_KM;
    });

    cameraSource.setData(buildCameraGeoJson(nearbyCameras));
  }, []);

  // Update clusters when map moves
  useEffect(() => {
    if (!map.current) return;

    const handleMapMove = () => {
      updateClusters();
    };

    map.current.on('moveend', handleMapMove);
    map.current.on('zoomend', handleMapMove);

    // Initial cluster update
    updateClusters();

    return () => {
      if (map.current) {
        map.current.off('moveend', handleMapMove);
        map.current.off('zoomend', handleMapMove);
      }
    };
  }, [updateClusters]);

  useEffect(() => {
    if (!map.current || !clusteringEnabled) {
      return;
    }
    updateClusters();
  }, [clusteringEnabled, updateClusters]);

  // Legacy marker update functions (kept for backward compatibility)
  useEffect(() => {
    if (!map.current || clusteringEnabled) {
      return;
    }

    updateMarkers(memoizedTokaPorts, tokaMarkers);
  }, [memoizedTokaPorts, clusteringEnabled]);

  useEffect(() => {
    if (!map.current || clusteringEnabled) {
      return;
    }

    updateMarkers(memoizedUgvPorts, ugvMarkers);
  }, [memoizedUgvPorts, clusteringEnabled]);

  useEffect(() => {
    if (!map.current || clusteringEnabled) {
      return;
    }

    updateKm220Markers(memoizedKm220Ports, km220Markers);
  }, [memoizedKm220Ports, clusteringEnabled, minPowerKw]);

  function showEmbedDistancePopup() {
    if (distancePopupRef.current) {
      distancePopupRef.current.remove();
      distancePopupRef.current = null;
    }
    const user = userLngLatRef.current;
    if (!user || !map.current || !memoizedKm220Ports.length) {
      return;
    }
    const portCoords = getEmbedPortCoords();
    if (!portCoords.length) {
      return;
    }
    let km = Infinity;
    let nearestCoord = portCoords[0];
    for (const coord of portCoords) {
      const d = haversineDistanceKm(user, coord);
      if (d < km) {
        km = d;
        nearestCoord = coord;
      }
    }
    const kmRounded = km >= 100 ? Math.round(km) : Math.round(km * 10) / 10;
    if (typeof onEmbedDistanceKm === 'function') {
      onEmbedDistanceKm(kmRounded);
    }
    if (!embedMode) {
      const isDayLightMap = isMapStyleDayLight(mapStyleRef.current);
      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -36],
        className: `map-distance-glass-popup map-distance-glass-popup--embed${
          isDayLightMap ? ' map-distance-glass-popup-daylight' : ''
        }`,
        maxWidth: '280px',
        anchor: 'bottom',
      })
        .setLngLat(nearestCoord)
        .setHTML(
          `<div class="map-distance-glass-inner"><span class="map-distance-glass-label">${distanceFromYouHtml(kmRounded)}</span></div>`
        )
        .addTo(map.current);
      const popupEl = popup.getElement();
      if (popupEl) {
        popupEl.style.zIndex = '40';
      }
      distancePopupRef.current = popup;
    }
  }

  function setEmbedUserMarker(lng, lat) {
    if (embedPortsOnly) {
      return;
    }
    if (!map.current || !Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    const lngLat = [lng, lat];
    if (embedUserMarkerRef.current) {
      embedUserMarkerRef.current.setLngLat(lngLat);
      return;
    }
    const el = document.createElement('div');
    el.className = 'map-embed-user-dot';
    embedUserMarkerRef.current = new maptilersdk.Marker({ element: el, anchor: 'center' })
      .setLngLat(lngLat)
      .addTo(map.current);
    const wrap = embedUserMarkerRef.current.getElement()?.parentElement;
    if (wrap) {
      wrap.style.zIndex = '4';
    }
  }

  function requestEmbedUserLocation() {
    if (embedGeoWatchRef.current) {
      return;
    }
    embedGeoWatchRef.current = true;
    const saved = getCookie('userLocation');
    if (saved?.latitude != null && saved?.longitude != null && !userLngLatRef.current) {
      userLngLatRef.current = [Number(saved.longitude), Number(saved.latitude)];
    }
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude, longitude } = pos.coords;
        try {
          setCookie('userLocation', JSON.stringify({ latitude, longitude }), 20);
        } catch (_e) {
          /* ignore */
        }
        applyEmbedUserLocation(longitude, latitude);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }

  function getEmbedViewShiftPx() {
    const h = mapContainer.current?.clientHeight ?? 0;
    return h > 0 ? Math.round(h * EMBED_VIEW_SHIFT_RATIO) : 0;
  }

  function getEmbedFitPadding() {
    const shift = getEmbedViewShiftPx();
    return {
      top: EMBED_FIT_PADDING_BASE.top + shift,
      bottom: EMBED_FIT_PADDING_BASE.bottom,
      left: EMBED_FIT_PADDING_BASE.left,
      right: EMBED_FIT_PADDING_BASE.right,
    };
  }

  function getEmbedPortCoords() {
    return memoizedKm220Ports
      .map(port => [parseFloat(port.longitude), parseFloat(port.latitude)])
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));
  }

  function extendEmbedPortBounds(bounds) {
    getEmbedPortCoords().forEach(coord => bounds.extend(coord));
  }

  function getEmbedPortsOnlyPadding() {
    if (embedSheetLayoutRef.current) {
      return getEmbedSheetMapPadding();
    }
    const shift = getEmbedViewShiftPx();
    const extraTop = embedExtraTopPadRef.current || 0;
    return {
      top: EMBED_PORTS_ONLY_TOP_PAD + shift + extraTop,
      bottom: EMBED_PORTS_ONLY_BOTTOM_PAD,
      left: EMBED_PORTS_ONLY_SIDE_PAD,
      right: EMBED_PORTS_ONLY_SIDE_PAD,
    };
  }

  function getEmbedMapViewportHeight() {
    return window.visualViewport?.height ?? window.innerHeight;
  }

  function getEmbedSheetVisibleMapHeightPx() {
    const h = mapContainer.current?.clientHeight || getEmbedMapViewportHeight();
    if (embedSheetExpandedRef.current) {
      return Math.max(120, h - START_MANY_SHEET_EXPANDED_PEEK_PX);
    }
    return Math.round(h * START_MANY_SHEET_COLLAPSED_TOP_RATIO);
  }

  function getEmbedSheetMapPadding() {
    const vh = getEmbedMapViewportHeight();
    const mapH = mapContainer.current?.clientHeight || vh;
    const visibleH = getEmbedSheetVisibleMapHeightPx();
    if (embedSheetExpandedRef.current) {
      return {
        top: EMBED_PORTS_ONLY_TOP_PAD,
        bottom: Math.max(START_MANY_SHEET_EXPANDED_PEEK_PX, mapH - visibleH),
        left: EMBED_PORTS_ONLY_SIDE_PAD,
        right: EMBED_PORTS_ONLY_SIDE_PAD,
      };
    }
    return {
      top: 0,
      bottom: Math.max(EMBED_PORTS_ONLY_BOTTOM_PAD, mapH - visibleH),
      left: EMBED_PORTS_ONLY_SIDE_PAD,
      right: EMBED_PORTS_ONLY_SIDE_PAD,
    };
  }

  function getEmbedSheetCenterOffset() {
    if (!embedSheetLayoutRef.current) {
      return [0, getEmbedViewShiftPx()];
    }
    const h = mapContainer.current?.clientHeight || getEmbedMapViewportHeight();
    const visibleH = getEmbedSheetVisibleMapHeightPx();
    return [0, Math.round(visibleH / 2 - h / 2)];
  }

  function getEmbedMapFitPadding() {
    if (embedSheetLayoutRef.current) {
      return getEmbedSheetMapPadding();
    }
    return getEmbedFitPadding();
  }

  function getEmbedPortsOnlyCenterOffset() {
    const shift = getEmbedViewShiftPx();
    const extraTop = embedExtraTopPadRef.current || 0;
    return [0, Math.round((EMBED_PORTS_ONLY_TOP_PAD + shift + extraTop) * 0.42)];
  }

  function centerEmbedMapOnPortsRegion() {
    if (!map.current || !memoizedKm220Ports.length) {
      return;
    }
    const portCoords = getEmbedPortCoords();
    if (!portCoords.length) {
      return;
    }
    const uniqueCoords = portCoords.filter(
      (coord, index) =>
        portCoords.findIndex(
          other => Math.abs(other[0] - coord[0]) < 0.0001 && Math.abs(other[1] - coord[1]) < 0.0001
        ) === index
    );
    if (uniqueCoords.length === 1) {
      map.current.easeTo({
        center: uniqueCoords[0],
        zoom: EMBED_MAP_FIT_MAX_ZOOM,
        offset: embedPortsOnlyRef.current ? getEmbedPortsOnlyCenterOffset() : getEmbedSheetCenterOffset(),
        duration: EMBED_MAP_MOTION_MS,
        easing: embedMapMotionEasing,
      });
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    uniqueCoords.forEach(coord => bounds.extend(coord));
    map.current.fitBounds(bounds, {
      padding: getEmbedUserAndPortPadding(),
      maxZoom: EMBED_MAP_FIT_MAX_ZOOM,
      duration: EMBED_MAP_MOTION_MS,
      easing: embedMapMotionEasing,
    });
  }

  function getEmbedPortsLayoutKey() {
    return (memoizedKm220Ports || []).map(port => String(port?.number ?? '')).join(',');
  }

  function markEmbedUserInteracted(e) {
    if (e?.originalEvent) {
      embedUserInteractedRef.current = true;
    }
  }

  function layoutEmbedMapIfNeeded() {
    const portsKey = getEmbedPortsLayoutKey();
    const portsChanged = portsKey !== embedLayoutPortsKeyRef.current;
    if (embedUserInteractedRef.current && !portsChanged) {
      return;
    }
    if (!portsChanged && embedLayoutPortsKeyRef.current) {
      return;
    }
    layoutEmbedMap();
    embedLayoutPortsKeyRef.current = portsKey;
    if (portsChanged) {
      embedUserInteractedRef.current = false;
    }
  }

  function embedUserSharesFrame(lng, lat) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return false;
    }
    return !isGpsFarFromUkraine(lat, lng, FAR_FROM_UKRAINE_KM);
  }

  function layoutEmbedMap() {
    const user = userLngLatRef.current;
    if (
      !embedPortsOnly &&
      !embedSheetLayoutRef.current &&
      user &&
      embedUserSharesFrame(user[0], user[1]) &&
      memoizedKm220Ports.length
    ) {
      fitEmbedMapToUserAndPort(user);
      return;
    }
    if (embedPortsOnly || embedSheetLayoutRef.current || embedMode) {
      centerEmbedMapOnPortsRegion();
      return;
    }
    if (userLngLatRef.current && memoizedKm220Ports.length) {
      fitEmbedMapToUserAndPort(userLngLatRef.current);
    } else if (memoizedKm220Ports.length) {
      centerEmbedMapOnPorts();
    }
  }

  function centerEmbedMapOnPorts() {
    if (!map.current || !memoizedKm220Ports.length) {
      return;
    }
    const coords = getEmbedPortCoords();
    if (!coords.length) {
      return;
    }
    if (coords.length === 1) {
      map.current.easeTo({
        center: coords[0],
        zoom: Math.min(map.current.getZoom() || EMBED_MAP_INITIAL_ZOOM, EMBED_MAP_FIT_MAX_ZOOM),
        offset: getEmbedSheetCenterOffset(),
        duration: 600,
      });
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    extendEmbedPortBounds(bounds);
    map.current.fitBounds(bounds, {
      padding: getEmbedMapFitPadding(),
      maxZoom: EMBED_MAP_FIT_MAX_ZOOM,
      duration: 600,
    });
  }

  function getEmbedPortMarkerHeight() {
    const marker = mapContainer.current?.querySelector('.embed-port-marker');
    const height = marker?.offsetHeight || 0;
    return Math.max(132, height + 16);
  }

  function getEmbedChromeCoverPx() {
    const mapEl = mapContainer.current;
    if (!mapEl) {
      return 0;
    }
    const navbar = document.querySelector('nav.fixed-top');
    if (!navbar) {
      return 0;
    }
    return Math.max(0, Math.round(navbar.getBoundingClientRect().bottom - mapEl.getBoundingClientRect().top));
  }

  function getEmbedUserAndPortPadding() {
    const h = mapContainer.current?.clientHeight ?? 280;
    const w = mapContainer.current?.clientWidth ?? 360;
    const bottom = 28;
    const side = Math.min(72, Math.round(w * 0.18));
    const wantedTop = getEmbedChromeCoverPx() + getEmbedPortMarkerHeight();
    const top = Math.min(wantedTop, Math.max(48, h - bottom - 40));
    return { top, bottom, left: side, right: side };
  }

  function fitEmbedMapToUserAndPort(userCoord) {
    if (!map.current || !userCoord || !memoizedKm220Ports.length) {
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    bounds.extend(userCoord);
    extendEmbedPortBounds(bounds);
    if (bounds.isEmpty()) {
      return;
    }
    const fitPadding = embedMode ? getEmbedUserAndPortPadding() : 48;
    map.current.fitBounds(bounds, {
      padding: fitPadding,
      maxZoom: embedMode ? EMBED_MAP_FIT_MAX_ZOOM : 12,
      duration: embedMode ? EMBED_MAP_MOTION_MS : 600,
      easing: embedMode ? embedMapMotionEasing : undefined,
    });
    embedFittedLngLatRef.current = userCoord;
  }

  function applyEmbedUserLocation(lng, lat) {
    const next = [lng, lat];
    const fitted = embedFittedLngLatRef.current;
    userLngLatRef.current = next;
    if (embedPortsOnly || !embedUserSharesFrame(lng, lat)) {
      showEmbedDistancePopup();
      return;
    }
    setEmbedUserMarker(lng, lat);
    if (embedSheetLayoutRef.current) {
      showEmbedDistancePopup();
      return;
    }
    if (embedUserInteractedRef.current) {
      showEmbedDistancePopup();
      return;
    }
    if (fitted && haversineDistanceKm(fitted, next) < 0.2) {
      showEmbedDistancePopup();
      return;
    }
    fitEmbedMapToUserAndPort(next);
    showEmbedDistancePopup();
  }

  function drawGeolocateControl() {
    map.current.on('load', () => {
      if (embedMode) {
        map.current.scrollZoom.enable();
        map.current.boxZoom.disable();
        map.current.dragRotate.disable();
        map.current.dragPan.enable();
        map.current.keyboard.disable();
        map.current.doubleClickZoom.enable();
        map.current.touchZoomRotate.enable();
        map.current.touchZoomRotate.disableRotation();
        map.current.touchPitch.disable();

        map.current.on('dragstart', markEmbedUserInteracted);
        map.current.on('zoomstart', markEmbedUserInteracted);
        map.current.on('rotatestart', markEmbedUserInteracted);
        map.current.on('pitchstart', markEmbedUserInteracted);

        updateKm220Markers(memoizedKm220Ports, km220Markers);
        const runEmbedLayout = () => {
          try {
            map.current?.resize();
          } catch (_e) {
            /* ignore */
          }
          requestEmbedUserLocation();
          layoutEmbedMapIfNeeded();
          const seededUser = userLngLatRef.current;
          if (seededUser && embedUserSharesFrame(seededUser[0], seededUser[1])) {
            setEmbedUserMarker(seededUser[0], seededUser[1]);
            showEmbedDistancePopup();
          }
          updateKm220Markers(embedPortsRef.current, km220Markers);
        };
        window.setTimeout(runEmbedLayout, 200);
        window.setTimeout(runEmbedLayout, 600);
        return;
      }

      const todayDateKey = getDateKeyFromTimestamp(Date.now());

      const savedTrackPoints = JSON.parse(localStorage.getItem(TRACK_LS_KEY) || '[]');
      const normalizedSavedTrackPoints = savedTrackPoints
        .filter(point => point?.lng != null && point?.lat != null)
        .map(point => ({
          lng: point.lng,
          lat: point.lat,
          ts: point.ts,
        }));

      const shouldClearSavedTrack =
        normalizedSavedTrackPoints.length > 0 &&
        getDateKeyFromTimestamp(normalizedSavedTrackPoints[normalizedSavedTrackPoints.length - 1].ts || 0) !==
          todayDateKey;

      if (shouldClearSavedTrack) {
        localStorage.removeItem(TRACK_LS_KEY);
        trackPointsRef.current = [];
        lastTrackSaveRef.current = null;
        lastTrackSampleTsRef.current = null;
      } else {
        trackPointsRef.current = normalizedSavedTrackPoints.map(point => [point.lng, point.lat]);
        const lastSavedTrackPoint = normalizedSavedTrackPoints[normalizedSavedTrackPoints.length - 1];
        lastTrackSaveRef.current = lastSavedTrackPoint?.ts ?? null;
        lastTrackSampleTsRef.current = lastSavedTrackPoint?.ts ?? null;
      }

      isTrackRecordingStoppedRef.current = false;

      map.current.addSource(TRACK_SOURCE_ID, {
        type: 'geojson',
        data: buildTrackGeoJson(trackPointsRef.current),
      });

      map.current.addLayer({
        id: TRACK_LAYER_ID,
        type: 'line',
        source: TRACK_SOURCE_ID,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#b44fe8',
          'line-width': 3,
          'line-opacity': 0.8,
        },
      });

      loadSpeedCamerasFromJsonl()
        .then(cameras => {
          speedCamerasRef.current = cameras;
          if (!map.current.getSource(CAMERA_SOURCE_ID)) {
            map.current.addSource(CAMERA_SOURCE_ID, {
              type: 'geojson',
              // Keep cameras hidden until a valid user GPS point is available.
              data: buildCameraGeoJson([]),
            });
          }

          if (!map.current.getLayer(CAMERA_LAYER_ID)) {
            if (!map.current.hasImage(CAMERA_ICON_ID)) {
              const iconImage = createCameraIconImage(28);
              if (iconImage) {
                map.current.addImage(CAMERA_ICON_ID, iconImage, { pixelRatio: 2 });
              }
            }
            map.current.addLayer({
              id: CAMERA_LAYER_ID,
              type: 'symbol',
              source: CAMERA_SOURCE_ID,
              layout: {
                'icon-image': CAMERA_ICON_ID,
                'icon-size': 0.75,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
              },
            });
          }
          if (userLngLatRef.current) {
            updateVisibleCamerasAroundUser(userLngLatRef.current);
          }
        })
        .catch(error => {
          console.error('Failed to load speed cameras:', error);
        });

      // Create main control container
      const controlContainer = document.createElement('div');
      controlContainer.className = 'maplibregl-ctrl maplibregl-ctrl-group';
      controlContainer.style.position = 'absolute';
      controlContainer.style.right = '10px';
      controlContainer.style.top = '60%';
      controlContainer.style.transform = 'scale(2.1) translateY(-50%)';
      controlContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.55)';
      controlContainer.style.borderRadius = '8px 0 0 8px';
      controlContainer.style.padding = '0px';
      controlContainer.style.zIndex = '2';

      // Create and add zoom in button
      const zoomInButton = document.createElement('button');
      zoomInButton.className = 'maplibregl-ctrl-zoom-in';
      zoomInButton.style.border = 'none';
      zoomInButton.style.width = '29px';
      zoomInButton.style.height = '29px';
      zoomInButton.style.cursor = 'pointer';
      zoomInButton.style.color = '#666';
      zoomInButton.style.fontSize = '18px';
      zoomInButton.style.display = 'flex';
      zoomInButton.style.alignItems = 'center';
      zoomInButton.style.justifyContent = 'center';
      zoomInButton.innerHTML = '+';
      zoomInButton.onclick = () => {
        const currentZoom = map.current.getZoom();
        map.current.easeTo({
          zoom: Math.min(currentZoom + ZOOM_BUTTON_STEP, 21),
          duration: ZOOM_ANIMATION_MS,
        });
      };

      // Create and add geolocate control
      const geolocateControl = new maplibregl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
        showAccuracyCircle: false,
        showUserLocation: true,
        fitBoundsOptions: {
          maxZoom: 20,
        },
      });

      // Create and add zoom out button
      const zoomOutButton = document.createElement('button');
      zoomOutButton.id = 'mapZoomOut';
      zoomOutButton.className = 'maplibregl-ctrl-zoom-out';
      zoomOutButton.style.border = 'none';
      zoomOutButton.style.width = '29px';
      zoomOutButton.style.height = '29px';
      zoomOutButton.style.cursor = 'pointer';
      zoomOutButton.style.color = '#666';
      zoomOutButton.style.fontSize = '18px';
      zoomOutButton.style.display = 'flex';
      zoomOutButton.style.alignItems = 'center';
      zoomOutButton.style.justifyContent = 'center';
      zoomOutButton.innerHTML = '−';
      zoomOutButton.onclick = () => {
        const currentZoom = map.current.getZoom();
        map.current.easeTo({
          zoom: Math.max(currentZoom - ZOOM_BUTTON_STEP, 0),
          duration: ZOOM_ANIMATION_MS,
        });
      };

      const reattachOverlaysAfterBasemapChange = () => {
        if (!map.current) return;
        try {
          if (!map.current.getSource(TRACK_SOURCE_ID)) {
            map.current.addSource(TRACK_SOURCE_ID, {
              type: 'geojson',
              data: buildTrackGeoJson(trackPointsRef.current),
            });
            map.current.addLayer({
              id: TRACK_LAYER_ID,
              type: 'line',
              source: TRACK_SOURCE_ID,
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': '#b44fe8',
                'line-width': 3,
                'line-opacity': 0.8,
              },
            });
          }
          if (!map.current.getSource(CAMERA_SOURCE_ID)) {
            map.current.addSource(CAMERA_SOURCE_ID, {
              type: 'geojson',
              data: buildCameraGeoJson([]),
            });
          }
          if (!map.current.getLayer(CAMERA_LAYER_ID)) {
            if (!map.current.hasImage(CAMERA_ICON_ID)) {
              const iconImage = createCameraIconImage(28);
              if (iconImage) {
                map.current.addImage(CAMERA_ICON_ID, iconImage, { pixelRatio: 2 });
              }
            }
            map.current.addLayer({
              id: CAMERA_LAYER_ID,
              type: 'symbol',
              source: CAMERA_SOURCE_ID,
              layout: {
                'icon-image': CAMERA_ICON_ID,
                'icon-size': 0.75,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
              },
            });
          }
        } catch (_e) {
          /* style may still be settling */
        }
        updateClusters();
      };

      const applyMapStyle = style => {
        if (!map.current) {
          return;
        }
        const satelliteExpected = style === 'hybrid';
        map.current.once('style.load', () => {
          applyMapLanguage(map.current);
          reattachOverlaysAfterBasemapChange();
          assertVectorBasemap(map.current, { satelliteExpected });
        });
        map.current.setStyle(mapStyleToVector(style));
      };

      const scheduleMapStyleExpiry = () => {
        if (mapStyleExpiryTimerRef.current) {
          window.clearTimeout(mapStyleExpiryTimerRef.current);
          mapStyleExpiryTimerRef.current = null;
        }
        mapStyleExpiryTimerRef.current = window.setTimeout(() => {
          mapStyleExpiryTimerRef.current = null;
          clearSavedMapStyle();
          const defaultStyle = getDefaultMapStyle(lat, lng);
          mapStyleRef.current = defaultStyle;
          setMapStyle(defaultStyle);
          applyMapStyle(defaultStyle);
          updateBasemapButton();
          updateDailyStatsPopupIfNeededRef.current();
        }, msUntilLocalMidnight());
      };

      // Create map style toggle button (cycles hybrid → white → night)
      const satelliteButton = document.createElement('button');
      satelliteButton.className = 'maplibregl-ctrl-satellite';
      satelliteButton.style.border = 'none';
      satelliteButton.style.borderBottom = '1px solid rgba(0, 0, 0, 0.1)';
      satelliteButton.style.width = '29px';
      satelliteButton.style.height = '29px';
      satelliteButton.style.cursor = 'pointer';
      satelliteButton.style.fontSize = '14px';
      satelliteButton.style.display = 'flex';
      satelliteButton.style.alignItems = 'center';
      satelliteButton.style.justifyContent = 'center';
      satelliteButton.style.backgroundColor = 'transparent';

      const updateBasemapButton = () => {
        const button = satelliteButtonRef.current;
        if (!button) {
          return;
        }
        switch (mapStyleRef.current) {
          case 'hybrid':
            button.innerHTML = mapIcon;
            button.title = 'Hybrid map — switch to white map';
            break;
          case 'night':
            button.innerHTML = moonIcon;
            button.title = 'Night map — switch to hybrid map';
            break;
          case 'white':
          default:
            button.innerHTML = earthIcon;
            button.title = 'White map — switch to night map';
            break;
        }
      };

      satelliteButtonRef.current = satelliteButton;
      updateBasemapButton();
      scheduleMapStyleExpiry();

      const deviceThemeMedia = window.matchMedia('(prefers-color-scheme: dark)');
      const onDeviceThemeChange = () => {
        if (getSavedMapStyle() || !map.current) {
          return;
        }
        const nextStyle = getDefaultMapStyle();
        if (mapStyleRef.current === nextStyle) {
          return;
        }
        mapStyleRef.current = nextStyle;
        setMapStyle(nextStyle);
        applyMapStyle(nextStyle);
        updateBasemapButton();
      };
      if (deviceThemeMediaRef.current && deviceThemeListenerRef.current) {
        deviceThemeMediaRef.current.removeEventListener('change', deviceThemeListenerRef.current);
      }
      deviceThemeMediaRef.current = deviceThemeMedia;
      deviceThemeListenerRef.current = onDeviceThemeChange;
      deviceThemeMedia.addEventListener('change', onDeviceThemeChange);

      satelliteButton.onclick = () => {
        const nextStyle = getNextMapStyle(mapStyleRef.current);
        mapStyleRef.current = nextStyle;
        setMapStyle(nextStyle);
        saveMapStyle(nextStyle);
        applyMapStyle(nextStyle);
        updateBasemapButton();
        scheduleMapStyleExpiry();
        updateDailyStatsPopupIfNeededRef.current();
      };

      // Compass sits above zoom and only appears once the map is rotated off north.
      const compassButton = document.createElement('button');
      compassButton.type = 'button';
      compassButton.className = 'map-ctrl-compass';
      compassButton.title = 'Reset north';
      compassButton.setAttribute('aria-label', 'Reset north');
      compassButton.style.display = 'none';
      compassButton.style.border = 'none';
      compassButton.style.borderBottom = '1px solid rgba(0, 0, 0, 0.1)';
      compassButton.style.width = '29px';
      compassButton.style.height = '29px';
      compassButton.style.padding = '0';
      compassButton.style.cursor = 'pointer';
      compassButton.style.backgroundColor = 'transparent';
      compassButton.style.alignItems = 'center';
      compassButton.style.justifyContent = 'center';
      const compassIcon = document.createElement('span');
      compassIcon.className = 'map-ctrl-compass-icon';
      compassIcon.innerHTML = compassIconSvg;
      compassButton.appendChild(compassIcon);

      const updateCompass = () => {
        if (!map.current) return;
        const bearing = map.current.getBearing();
        const rotated = Math.abs(bearing) > 0.5;
        compassButton.style.display = rotated ? 'flex' : 'none';
        compassIcon.style.transform = `rotate(${-bearing}deg)`;
      };
      compassButton.onclick = event => {
        event.preventDefault();
        event.stopPropagation();
        map.current?.resetNorth({ duration: ZOOM_ANIMATION_MS });
      };
      map.current.on('rotate', updateCompass);
      updateCompass();

      // Add all buttons to container
      controlContainer.appendChild(satelliteButton);
      controlContainer.appendChild(compassButton);
      controlContainer.appendChild(zoomInButton);
      map.current.addControl(geolocateControl, 'top-right');
      const geolocateButton = document.querySelector('.maplibregl-ctrl-geolocate');
      if (geolocateButton) {
        geolocateButton.style.border = 'none';
        geolocateButton.style.borderTop = '1px solid rgba(0, 0, 0, 0.1)';
        geolocateButton.style.borderBottom = '1px solid rgba(0, 0, 0, 0.1)';
        geolocateButton.style.borderRadius = '0';
        geolocateButton.style.color = '#666';
        geolocateButton.addEventListener('click', () => {
          if (map.current) {
            const center = map.current.getCenter();
            preGeolocateViewRef.current = {
              lng: center.lng,
              lat: center.lat,
              zoom: map.current.getZoom(),
            };
          }
        });
        controlContainer.appendChild(geolocateButton);
      }
      controlContainer.appendChild(zoomOutButton);

      // Add the container to the map
      mapContainer.current.appendChild(controlContainer);

      // Store geolocate control reference
      geolocateControlRef.current = geolocateControl;

      // Handle geolocate event - start following on first click
      geolocateControl.on('geolocate', e => {
        userLngLatRef.current = [e.coords.longitude, e.coords.latitude];
        updateCameraPopupIfNeededRef.current(userLngLatRef.current);
        const trackCoord = [e.coords.longitude, e.coords.latitude];
        const now = Date.now();
        const nowDateKey = getDateKeyFromTimestamp(now);
        const lastSampleDateKey = lastTrackSampleTsRef.current
          ? getDateKeyFromTimestamp(lastTrackSampleTsRef.current)
          : null;

        // Clear current line and storage when day changes (night rollover).
        if (lastSampleDateKey && lastSampleDateKey !== nowDateKey) {
          trackPointsRef.current = [];
          lastTrackSaveRef.current = null;
          lastTrackSampleTsRef.current = null;
          localStorage.removeItem(TRACK_LS_KEY);
          updateDailyStatsPopupIfNeededRef.current();
        }

        const previousTrackCoord = trackPointsRef.current[trackPointsRef.current.length - 1] || null;
        const previousTrackTs = lastTrackSampleTsRef.current;
        let isSuspiciousGpsDrop = false;
        let isTooFarDrop = false;

        if (previousTrackCoord) {
          const distanceFromPrevKm = haversineDistanceKm(previousTrackCoord, trackCoord);
          isTooFarDrop = distanceFromPrevKm > TRACK_STOP_DISTANCE_KM;

          if (previousTrackTs && now > previousTrackTs) {
            const elapsedHours = (now - previousTrackTs) / (60 * 60 * 1000);
            if (elapsedHours > 0) {
              const speedKmh = distanceFromPrevKm / elapsedHours;
              isSuspiciousGpsDrop = speedKmh > TRACK_MAX_REASONABLE_SPEED_KMH;
            }
          }
        }

        if (isTooFarDrop) {
          isTrackRecordingStoppedRef.current = true;
          console.warn('Track recording stopped due to point jump greater than 500 km.');
        }

        const canRecordTrackPoint = !isTrackRecordingStoppedRef.current && !isSuspiciousGpsDrop;
        if (canRecordTrackPoint) {
          trackPointsRef.current.push(trackCoord);
          lastTrackSampleTsRef.current = now;

          const trackSource = map.current.getSource(TRACK_SOURCE_ID);
          if (trackSource) {
            trackSource.setData(buildTrackGeoJson(trackPointsRef.current));
          }

          if (!lastTrackSaveRef.current || now - lastTrackSaveRef.current >= TRACK_SAVE_INTERVAL_MS) {
            const storedTrackPoints = JSON.parse(localStorage.getItem(TRACK_LS_KEY) || '[]');
            const lastStoredTrackPoint = storedTrackPoints[storedTrackPoints.length - 1] || null;
            const isCloseToLastStoredPoint =
              lastStoredTrackPoint &&
              haversineDistanceKm([lastStoredTrackPoint.lng, lastStoredTrackPoint.lat], trackCoord) <
                TRACK_MIN_STORE_DISTANCE_KM;

            if (!isCloseToLastStoredPoint) {
              lastTrackSaveRef.current = now;
              storedTrackPoints.push({
                lng: trackCoord[0],
                lat: trackCoord[1],
                ts: now,
              });
              localStorage.setItem(TRACK_LS_KEY, JSON.stringify(storedTrackPoints));
              enqueueTrackPoint({
                lat: trackCoord[1],
                lng: trackCoord[0],
                ts: now,
                source: 'gps',
              });
            }
          }
        }

        updateDailyStatsPopupIfNeededRef.current();
        updateVisibleCamerasAroundUser(userLngLatRef.current);

        const gpsLat = e.coords.latitude;
        const gpsLng = e.coords.longitude;
        if (isGpsFarFromUkraine(gpsLat, gpsLng, FAR_FROM_UKRAINE_KM)) {
          // Abroad GPS: keep Ukraine map at region zoom — do not fly to user.
          isFollowingRef.current = false;
          setIsFollowing(false);
          isFirstGeolocateClick.current = true;
          if (!isFarGpsAirAlertDismissedToday()) {
            setFarGpsAirAlertOpen(true);
          }
          try {
            map.current?.stop();
          } catch (_e) {
            /* ignore */
          }
          const saved = preGeolocateViewRef.current;
          const stayLng = saved?.lng ?? map.current.getCenter().lng;
          const stayLat = saved?.lat ?? map.current.getCenter().lat;
          map.current.jumpTo({
            center: [stayLng, stayLat],
            zoom: MAP_REGION_LEVEL_ZOOM,
          });
          // Stop active tracking so the control does not keep panning abroad.
          try {
            const state = geolocateControl._watchState;
            if (state === 'ACTIVE_LOCK' || state === 'ACTIVE_ERROR' || state === 'WAITING_ACTIVE') {
              geolocateControl.trigger();
            }
          } catch (_e) {
            /* ignore private API differences */
          }
          return;
        }

        setFarGpsAirAlertOpen(false);

        if (isFirstGeolocateClick.current) {
          // First click: zoom to 20 and start following
          isFirstGeolocateClick.current = false;
          isFollowingRef.current = true;
          setIsFollowing(true);
          map.current.flyTo({
            center: [e.coords.longitude, e.coords.latitude],
            zoom: 20,
            duration: 2000,
          });
        } else if (isFollowingRef.current) {
          // Continue following: update center smoothly
          map.current.easeTo({
            center: [e.coords.longitude, e.coords.latitude],
            duration: 1000,
          });
        }
        requestAnimationFrame(() => updateDistancePopupIfNeededRef.current());
      });

      // Handle trackuserlocationstart event - enable follow mode when tracking starts
      geolocateControl.on('trackuserlocationstart', () => {
        if (!isFirstGeolocateClick.current) {
          isFollowingRef.current = true;
          setIsFollowing(true);
        }
      });

      // Handle trackuserlocationend event - disable follow mode when tracking stops
      geolocateControl.on('trackuserlocationend', () => {
        isFollowingRef.current = false;
        setIsFollowing(false);
      });

      // Hide location circles
      const locationCircle = document.querySelector('.maplibregl-user-location-dot');
      const locationAccuracyCircle = document.querySelector('.maplibregl-user-location-accuracy-circle');

      if (locationCircle) {
        locationCircle.style.display = 'none';
        locationCircle.style.zIndex = '0';
      }
      if (locationAccuracyCircle) {
        locationAccuracyCircle.style.display = 'none';
        locationAccuracyCircle.style.zIndex = '0';
      }
    });
  }

  function updateMarkers(ports, markers) {
    markers.forEach(function (marker) {
      marker.remove();
    });

    ports?.forEach(function (port) {
      var { marker, el } = drawMarkerWithLocation(port);

      el.style.backgroundImage = port?.name?.includes('OVERPRICED')
        ? 'url(/portRoaming20.svg)'
        : `url(/portRoaming.svg)`;
      el.style.width = port?.name?.includes('OVERPRICED') ? '20px' : `30px`;
      el.style.height = port?.name?.includes('OVERPRICED') ? '20px' : `30px`;

      el.addEventListener('click', function () {
        // ReactGA.event({
        //   action: port?.deviceId,
        // });
        window.open(port?.deviceId);
      });

      el.className = port?.name?.includes('IN_PROGRESS') ? 'blink_me' : null;

      markers.push(new maptilersdk.Marker({ element: el }).setLngLat(marker.geometry.coordinates).addTo(map.current));
    });

    // console.log(markers);
  }

  function extractOcppPortNumber(name) {
    const match = name?.match(/ocppPortNumber:(\d+)/);
    return match ? match[1] : null;
  }

  function getEmbedClusterConnectorSortKey(port) {
    const ocpp = extractOcppPortNumber(port?.name);
    if (ocpp != null && ocpp !== '') {
      return Number(ocpp);
    }
    return Number(port?.number) || 0;
  }

  function createEmbedPortClusterElement(group) {
    const shell = document.createElement('div');
    shell.className = 'embed-port-marker';

    const el = document.createElement('div');
    el.className = 'embed-port-cluster';

    const sorted = [...group].sort((a, b) => getEmbedClusterConnectorSortKey(a) - getEmbedClusterConnectorSortKey(b));
    const priceUnit = i18n.t('portPriceStickerUnit');

    el.innerHTML = buildEmbedPortClusterRowsHtml({
      ports: sorted,
      statuses: embedPortStatusesRef.current,
      plugTypeMap: {
        ...plugTypeMaping,
        TYPE_2_NO_CABBLE: `TYPE 2 ${i18n.t('plugTypeNoCable')}`,
      },
      getConnectorNum: (port, index) => extractOcppPortNumber(port?.name) || String(index + 1),
      getStatusClass: getEmbedPortStatusClass,
      priceUnit,
      selectedNumber: focusStationNumberRef.current,
    });

    const clusterBody = wrapEmbedClusterWithFeatureBadges(el, {
      hasWebcam: sorted.some(portHasWebcam),
      hasEnergyStorage: sorted.some(portHasEnergyStorage),
    });

    const onClusterActivate = () => {
      embedOnPortClusterClickRef.current?.(sorted);
    };

    if (embedOnPortClusterClickRef.current) {
      el.classList.add('embed-port-cluster--clickable');
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.addEventListener('click', event => {
        event.stopPropagation();
        onClusterActivate();
      });
      el.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          onClusterActivate();
        }
      });
    }

    const stem = document.createElement('span');
    stem.className = 'embed-port-marker__stem';
    stem.setAttribute('aria-hidden', 'true');

    const dot = document.createElement('span');
    dot.className = 'embed-port-marker__dot';
    dot.setAttribute('aria-hidden', 'true');

    shell.appendChild(clusterBody);
    shell.appendChild(stem);
    shell.appendChild(dot);
    return shell;
  }

  /** Strip ocppPortNumber suffix appended by backend to station name (e.g. ...2404ocppPortNumber:1 -> ...2404) */
  function stripOcppPortNumberFromPromoLink(url) {
    return url ? url.replace(/ocppPortNumber:(?:\d+|unknown)$/, '') : url;
  }

  function addKm220MarkerElement(port, lngLat, markers) {
    var { el } = drawMarkerWithLocation({ ...port, longitude: lngLat[0], latitude: lngLat[1] });

    const ocppPortNumber = extractOcppPortNumber(port?.name);

    let promoLink = null;
    if (port?.name?.includes('promoLink:')) {
      const match = port.name.match(/promoLink:([^\s]+)/);
      if (match && match[1]) {
        promoLink = stripOcppPortNumberFromPromoLink(match[1]);
      }
      el.style.backgroundImage = `url(/portRoaming.svg)`;
      el.style.width = '30px';
      el.style.height = '30px';
    } else if (port?.name?.includes('inService')) {
      el.style.backgroundImage = `url(/port/inService/1.svg)`;
    } else if (port?.name?.includes('dick')) {
      el.style.backgroundImage = `url(/moscow.svg)`;
    } else {
      el.className = port?.job?.state === 'IN_PROGRESS' || port?.name?.includes('used') ? 'blink_me' : null;
      if (ocppPortNumber) {
        el.style.backgroundImage = port.online
          ? `url(/port/online/${ocppPortNumber}.svg)`
          : `url(/port/offline/${ocppPortNumber}.svg)`;
      } else {
        el.style.backgroundImage = port.online ? `url(/port/online/1.svg)` : `url(/port/offline/1.svg)`;
      }
    }

    if (!embedMode) {
      el.addEventListener('click', function () {
        if (map.current) {
          const currentZoom = map.current.getZoom();
          const currentCenter = map.current.getCenter();
          sessionStorage.setItem('mapZoom', currentZoom);
          sessionStorage.setItem('mapCenter', JSON.stringify([currentCenter.lng, currentCenter.lat]));
        }
        if (promoLink) {
          window.open(promoLink);
        } else {
          window.location.href = `/start?station=${port.number}`;
        }
      });
    }

    markers.push(new maptilersdk.Marker({ element: el }).setLngLat(lngLat).addTo(map.current));
  }

  function updateKm220Markers(ports, markers) {
    markers.forEach(function (marker) {
      marker.remove();
    });

    if (!ports?.length || !map.current) {
      return;
    }

    const locationGroups = new globalThis.Map();
    ports.forEach(function (port) {
      if (port?.name?.includes('doNotShowOnMap')) {
        return;
      }
      const key = embedMode ? getEmbedPortGroupKey(port) : getPortLocationGroupKey(port);
      if (!key) {
        return;
      }
      if (!locationGroups.has(key)) {
        locationGroups.set(key, []);
      }
      locationGroups.get(key).push(port);
    });

    locationGroups.forEach(function (group) {
      const baseLng = parseFloat(group[0].longitude);
      const baseLat = parseFloat(group[0].latitude);
      const baseLngLat = [baseLng, baseLat];

      if (embedMode) {
        const el = createEmbedPortClusterElement(group);
        markers.push(
          new maptilersdk.Marker({ element: el, anchor: 'bottom' }).setLngLat(baseLngLat).addTo(map.current)
        );
        return;
      }

      const portNumbers = group.map(p => p?.number).filter(Boolean);
      const powerKw = getGroupPowerKw(group);
      if (!powerMeetsMinKw(powerKw, minPowerKw)) {
        return;
      }
      const el = createKm220MapPinElement(group[0], {
        powerKw,
        portNumbers,
        saveMapState: true,
        offline: areAllPortsOffline(group),
        hasWebcam: group.some(portHasWebcam),
        hasEnergyStorage: group.some(portHasEnergyStorage),
        blink: group.some(portIsCharging),
      });
      markers.push(new maptilersdk.Marker({ element: el }).setLngLat(baseLngLat).addTo(map.current));
    });
  }

  function drawMarkerWithLocation(port) {
    var marker = {
      type: 'Feature',
      properties: {
        message: 'Baz',
      },
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(`${port.longitude}`), parseFloat(`${port.latitude}`)],
      },
    };

    var el = document.createElement('div');
    el.style.width = '40px';
    el.style.height = '40px';
    el.style.zIndex = '1'; // Ensure markers have a higher z-index than the location circle
    return { marker, el };
  }

  function isIos() {
    return (
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
      // iPad on isIos 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }

  useEffect(() => {
    if (!embedMode || !map.current || clusteringEnabled) {
      return;
    }
    updateKm220Markers(memoizedKm220Ports, km220Markers);
  }, [
    embedMode,
    memoizedKm220Ports,
    clusteringEnabled,
    embedPortStatuses,
    embedPortsOnly,
    onEmbedPortClusterClick,
    focusStationNumber,
  ]);

  useEffect(() => {
    if (!embedMode || !map.current || clusteringEnabled) {
      return;
    }
    layoutEmbedMapIfNeeded();
    if (userLngLatRef.current) {
      showEmbedDistancePopup();
    }
  }, [embedMode, memoizedKm220Ports, clusteringEnabled, embedPortsOnly]);

  useEffect(() => {
    if (!embedMode || !map.current || clusteringEnabled || !embedSheetLayout) {
      return;
    }
    layoutEmbedMap();
    const timer = window.setTimeout(() => {
      try {
        map.current?.resize();
      } catch (_e) {
        /* ignore */
      }
      updateKm220Markers(embedPortsRef.current, km220Markers);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [embedMode, embedSheetLayout, embedSheetExpanded, clusteringEnabled, memoizedKm220Ports]);

  useEffect(() => {
    if (embedMode || !map.current || !flyToStation) {
      return;
    }
    const lng = parseFloat(flyToStation.longitude);
    const lat = parseFloat(flyToStation.latitude);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    if (isFollowingRef.current) {
      isFollowingRef.current = false;
      setIsFollowing(false);
    }
    const targetZoom = Math.max(map.current.getZoom(), 16);
    map.current.flyTo({
      center: [lng, lat],
      zoom: targetZoom,
      duration: 1200,
    });
    sessionStorage.setItem('mapZoom', String(targetZoom));
    sessionStorage.setItem('mapCenter', JSON.stringify([lng, lat]));
  }, [embedMode, flyToStation]);

  /* Full-page map: keep canvas sized to the visible viewport (iOS WKWebView / visualViewport). */
  useEffect(() => {
    if (embedMode) {
      return;
    }

    let resizeRaf = null;
    const scheduleMapViewportSync = () => {
      syncMapViewportHeightCss();
      if (resizeRaf != null) {
        window.cancelAnimationFrame(resizeRaf);
      }
      resizeRaf = window.requestAnimationFrame(() => {
        resizeRaf = null;
        try {
          map.current?.resize();
        } catch (_e) {
          /* ignore */
        }
      });
    };

    scheduleMapViewportSync();
    const delayedSync1 = window.setTimeout(scheduleMapViewportSync, 200);
    const delayedSync2 = window.setTimeout(scheduleMapViewportSync, 600);

    window.addEventListener('resize', scheduleMapViewportSync);
    window.addEventListener('orientationchange', scheduleMapViewportSync);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', scheduleMapViewportSync);
    visualViewport?.addEventListener('scroll', scheduleMapViewportSync);

    return () => {
      window.clearTimeout(delayedSync1);
      window.clearTimeout(delayedSync2);
      if (resizeRaf != null) {
        window.cancelAnimationFrame(resizeRaf);
      }
      window.removeEventListener('resize', scheduleMapViewportSync);
      window.removeEventListener('orientationchange', scheduleMapViewportSync);
      visualViewport?.removeEventListener('resize', scheduleMapViewportSync);
      visualViewport?.removeEventListener('scroll', scheduleMapViewportSync);
      document.documentElement.style.removeProperty('--map-viewport-height');
    };
  }, [embedMode]);

  return (
    <div
      className={embedMode ? 'map-wrap map-wrap--embed' : 'map-wrap'}
      style={embedMode ? { height: embedHeight } : undefined}
    >
      <div
        ref={mapContainer}
        className="map"
        style={{
          opacity: isLoading ? 0.1 : 1,
          transition: 'opacity 0.3s ease',
        }}
      />
      {isLoading && (
        <div className="map-loader-overlay">
          <Spinner />
        </div>
      )}
      {!embedMode && farGpsAirAlertOpen && typeof document !== 'undefined'
        ? createPortal(
            <div className="map-far-gps-air-alert" role="alert">
              <p className="map-far-gps-air-alert__text">{t('mapFarGpsAirAlert')}</p>
              <button
                type="button"
                className="map-far-gps-air-alert__dismiss"
                onClick={() => {
                  dismissFarGpsAirAlertForToday();
                  setFarGpsAirAlertOpen(false);
                }}
              >
                {t('mapFarGpsAirAlertDismiss')}
              </button>
            </div>,
            document.body
          )
        : null}
      {!embedMode && (
        <ClusterControls
          onToggleClustering={handleToggleClustering}
          clusteringEnabled={clusteringEnabled}
          onRadiusChange={handleRadiusChange}
          clusterRadius={clusterRadius}
        />
      )}
      {!embedMode && evuaMapsTarget ? (
        <OpenMapsConfirmModal
          modalOpen={Boolean(evuaMapsTarget)}
          setModalOpen={open => {
            if (!open) {
              setEvuaMapsTarget(null);
              if (!embedMode) {
                setSearchParams(withSelectedStationSearchParams(searchParams, null), { replace: true });
              }
            }
          }}
          latitude={evuaMapsTarget.latitude}
          longitude={evuaMapsTarget.longitude}
          evuaUrl={evuaMapsTarget.evuaUrl}
          evuaPort={evuaMapsTarget.port}
          portFilter={evuaMapsTarget.portFilter ?? portFilter}
          stationKey={evuaMapsTarget.stationKey}
        />
      ) : null}
    </div>
  );
}
